<template>
  <div class="group-workspace-panel" id="groupWorkspacePanel" :class="{ 'is-cloud': currentGroupWorkspaceTab === 'cloud', 'is-preview': currentGroupWorkspaceTab === 'preview', 'is-files': currentGroupWorkspaceTab === 'files' || currentGroupWorkspaceTab === 'artifacts' }">
    <div class="group-workspace-tabs" id="groupWorkspaceTabs">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="group-workspace-tab"
        :class="{ active: currentGroupWorkspaceTab === tab.key }"
        type="button"
        :data-group-workspace-tab="tab.key"
        @click="setTab(tab.key)"
      >{{ tab.label }}</button>
    </div>

    <div v-if="currentGroupWorkspaceTab === 'changes'" class="gw-changes-panel">
      <!-- 文件选择器 -->
      <div class="gw-changes-selector">
        <span class="gw-changes-selector-label">选择文件</span>
        <div class="gw-changes-custom-select" :class="{ open: changesDropdownOpen }">
          <div class="gw-changes-select-trigger" @click="changesDropdownOpen = !changesDropdownOpen">
            <span :class="{ placeholder: !changesSelectedFile }">{{ changesSelectedFileName || '请选择文件' }}</span>
            <span class="gw-changes-select-arrow">▾</span>
          </div>
          <div v-if="changesDropdownOpen" class="gw-changes-select-dropdown">
            <input class="gw-changes-select-search" type="text" v-model="changesFileQuery" placeholder="搜索文件..." @click.stop />
            <div class="gw-changes-select-list">
              <div
                v-for="f in filteredChangesFiles"
                :key="f.id"
                class="gw-changes-select-option"
                :class="{ active: changesSelectedFile === f.id }"
                @click="selectChangesFile(f.id)"
              >{{ f.title }}</div>
              <div v-if="filteredChangesFiles.length === 0" class="gw-changes-select-empty">无匹配文件</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 未选择文件提示 -->
      <template v-if="!changesSelectedFile">
        <div class="gw-changes-empty">
          <div class="gw-changes-empty-text">{{ changesAllFiles.length ? '请选择一个文件查看版本历史' : '当前会话暂无文件' }}</div>
        </div>
      </template>

      <template v-else>
        <!-- 加载中 -->
        <div v-if="fileVersionsLoading" class="gw-changes-loading">
          <div class="gw-files-loading-dot"></div>
          <div class="gw-files-loading-dot"></div>
          <div class="gw-files-loading-dot"></div>
        </div>

        <template v-else>
          <!-- 三个统计卡片 -->
          <div class="gw-changes-stats">
            <div class="gw-changes-stat-card accent-pink">
              <div class="gw-changes-stat-value">{{ changesTotalCount }}</div>
              <div class="gw-changes-stat-label">修改次数</div>
            </div>
            <div class="gw-changes-stat-card accent-green">
              <div class="gw-changes-stat-value">{{ changesTodayCount }}</div>
              <div class="gw-changes-stat-label">今日修改次数</div>
            </div>
            <div class="gw-changes-stat-card accent-blue">
              <div class="gw-changes-stat-value">{{ changesWeekCount }}</div>
              <div class="gw-changes-stat-label">近7日修改次数</div>
            </div>
          </div>
          <!-- 按日期分组的变更文件列表 -->
          <div class="gw-changes-list">
            <template v-for="group in changesGroupedByDate">
              <div :key="'d-' + group.date" class="gw-changes-date-header">{{ group.dateLabel }}</div>
              <div
                v-for="item in group.items"
                :key="item.id"
                class="gw-changes-card"
              >
                <div class="gw-changes-card-top">
                  <div class="gw-changes-card-avatar">文</div>
                  <div class="gw-changes-card-info">
                    <div class="gw-changes-card-title">{{ item.title }}</div>
                    <div class="gw-changes-card-author">{{ item.author }}</div>
                    <div class="gw-changes-card-time">{{ item.time }}</div>
                  </div>
                  <div class="gw-changes-card-actions">
                    <button type="button" class="gw-changes-action-btn" @click.stop="downloadChangesItem(item)">下载</button>
                    <button type="button" class="gw-changes-action-btn primary" @click.stop="onChangesItemClick(item)">预览</button>
                  </div>
                </div>
                <div v-if="item.summary" class="gw-changes-card-summary">修改简介：{{ item.summary }}</div>
                <button v-if="item.summary" type="button" class="gw-changes-card-expand">展开</button>
              </div>
            </template>
            <div v-if="changesGroupedByDate.length === 0" class="gw-changes-empty">
              <div class="gw-changes-empty-text">暂无文件变更记录</div>
            </div>
          </div>
        </template>
      </template>
    </div>

    <div v-else-if="currentGroupWorkspaceTab !== 'changes'" class="group-workspace-shell">
      <aside class="group-workspace-sidebar">
        <div>
          <div class="group-workspace-sidebar-title" id="groupWorkspaceSidebarTitle">{{ sidebarTitle }}</div>
          <div class="group-workspace-sidebar-sub" id="groupWorkspaceSidebarSub">{{ sidebarSub }}</div>
        </div>
        <div class="group-workspace-list" id="groupWorkspaceList">
          <button
            v-for="item in workspaceItems"
            :key="item.id"
            class="group-workspace-item"
            :class="[item.opensPreview ? 'file' : (item.previewMode === 'folder' ? 'folder' : 'group'), { active: isItemSelected(item) }]"
            type="button"
            :data-group-workspace-item="item.id"
            :style="item.level ? { '--group-indent': (item.level * 16) + 'px' } : {}"
            @click="selectItem(item)"
          >
            <div class="group-workspace-item-head">
              <div class="group-workspace-item-icon">{{ item.icon || '📄' }}</div>
              <div class="group-workspace-item-copy">
                <div class="group-workspace-item-title">{{ item.title || item.name }}</div>
                <div v-if="item.meta" class="group-workspace-item-meta">{{ item.meta }}</div>
              </div>
            </div>
            <span v-if="item.tag" class="group-workspace-item-tag" :class="item.tagClass">{{ item.tag }}</span>
          </button>
        </div>
      </aside>
      <section class="group-workspace-main">
        <div class="group-workspace-preview-head">
          <div class="group-workspace-preview-file" id="groupWorkspacePreviewFile">{{ previewFile }}</div>
          <div class="group-workspace-preview-meta" id="groupWorkspacePreviewMeta">{{ previewMeta }}</div>
        </div>
        <div class="group-workspace-preview-body" id="groupWorkspacePreviewBody">

          <!-- Files tab: agent 走独立文件树布局，非 agent 走原有文件列表 -->
          <template v-if="currentGroupWorkspaceTab === 'files' && currentSessionType !== 'agent'">
            <div class="gw-files-panel">
              <!-- filter bar -->
              <div class="gw-files-filter-bar">
                <div class="gw-files-filter-pills">
                  <button
                    v-for="f in fileFilters"
                    :key="f.key"
                    type="button"
                    class="gw-files-filter-btn"
                    :class="{ active: activeFileFilter === f.key }"
                    @click="activeFileFilter = f.key"
                  >{{ f.label }}</button>
                </div>
                <span v-if="filesTotal > 0" class="gw-files-total">共 {{ filesTotal }} 个文件</span>
              </div>
              <div v-if="conversationFilesLoading" class="gw-files-loading">
                <div class="gw-files-loading-dot"></div>
                <div class="gw-files-loading-dot"></div>
                <div class="gw-files-loading-dot"></div>
              </div>
              <div v-else-if="filteredApiFiles.length === 0" class="gw-files-empty">
                <svg class="gw-files-empty-svg" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="20" y="30" width="80" height="70" rx="8" fill="#EEF2FF" stroke="#C7D2FE" stroke-width="2"/>
                  <rect x="30" y="20" width="60" height="70" rx="8" fill="#F5F7FF" stroke="#C7D2FE" stroke-width="2"/>
                  <path d="M45 55h30M45 67h20" stroke="#A5B4FC" stroke-width="2.5" stroke-linecap="round"/>
                  <circle cx="85" cy="85" r="18" fill="#EEF2FF" stroke="#C7D2FE" stroke-width="2"/>
                  <path d="M79 85h12M85 79v12" stroke="#A5B4FC" stroke-width="2.5" stroke-linecap="round"/>
                </svg>
                <div class="gw-files-empty-title">暂无文件</div>
                <div class="gw-files-empty-sub">在对话中发送文件，它们会出现在这里</div>
              </div>
              <!-- media grid (媒体分类缩略图) -->
              <div v-else-if="activeFileFilter === 'media'" class="gw-media-grid" @scroll="onFilesScroll">
                <div v-for="item in filteredApiFiles" :key="item.id" class="gw-media-item" @click="openPreview(item)">
                  <div class="gw-media-inner">
                    <img v-if="item.kind === 'image'" :src="item.url" class="gw-media-thumb" loading="lazy" />
                    <video v-else-if="item.kind === 'video'" :src="item.url" class="gw-media-thumb" preload="metadata" muted @loadedmetadata="e => { e.target.currentTime = 0.1 }"></video>
                    <div v-else class="gw-media-thumb gw-media-audio">🎵</div>
                    <div v-if="item.kind === 'video'" class="gw-media-play-center"><svg viewBox="0 0 24 24" fill="#fff" width="28" height="28"><path d="M8 5v14l11-7z"/></svg></div>
                    <span class="gw-media-ext">.{{ item.ext || 'file' }}</span>
                  </div>
                </div>
              </div>
              <!-- link list (链接分类) -->
              <div v-else-if="activeFileFilter === 'link'" class="gw-files-list" @scroll="onFilesScroll">
                <div v-for="item in filteredApiFiles" :key="item.id || item.url" class="gw-link-row" @click="openLink(item)">
                  <div class="gw-link-icon" :style="{ background: linkIconColor(item.url || item.title) }">{{ linkInitial(item.title) }}</div>
                  <div class="gw-link-info">
                    <div class="gw-link-title">{{ item.title || item.url || '未知链接' }}</div>
                    <div class="gw-link-url">{{ item.url }}</div>
                  </div>
                </div>
              </div>
              <!-- file list -->
              <div v-else class="gw-files-list" @scroll="onFilesScroll">
                <div v-for="item in filteredApiFiles" :key="item.id" class="gw-file-row">
                  <div class="gw-file-row-icon" :class="gwIconClass(item)">
                    <svg v-if="item.kind === 'image'" viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>
                    <svg v-else-if="item.kind === 'video'" viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/></svg>
                    <svg v-else-if="item.kind === 'audio'" viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg>
                    <svg v-else-if="item.ext === 'pdf'" viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z"/></svg>
                    <svg v-else viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/></svg>
                  </div>
                  <div class="gw-file-row-info">
                    <div class="gw-file-row-name">{{ item.title }}</div>
                    <div class="gw-file-row-meta">{{ item.senderMeta || item.meta }}</div>
                  </div>
                  <div class="gw-file-row-actions">
                    <button v-if="item.isAgent" type="button" class="gw-file-action-btn" @click.stop="openFileHistory(item)">历史</button>
                    <button type="button" class="gw-file-action-btn" @click.stop="downloadFile(item)">下载</button>
                    <button type="button" class="gw-file-action-btn primary" @click.stop="openPreview(item)">预览</button>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Files tab: agent 会话 - 文件树布局 -->
          <template v-else-if="currentGroupWorkspaceTab === 'files' && currentSessionType === 'agent'">
            <div class="aft-wrap">
              <!-- 左侧边栏 -->
              <aside class="aft-sidebar">
                <div v-if="!currentFolderTree" class="aft-sidebar-empty">暂无文件</div>
                <template v-else>
                  <div
                    v-for="folder in currentFolderTree.folders"
                    :key="folder.id"
                    class="aft-sb-folder"
                  >
                    <!-- 文件夹头 -->
                    <div
                      class="aft-sb-folder-head"
                      :class="{ active: aftActiveFolderId === folder.id && !aftActiveFileId }"
                      :style="{ paddingLeft: (8 + folder.depth * 14) + 'px' }"
                      @click="aftSelectFolder(folder)"
                    >
                      <span class="aft-sb-folder-icon" :style="folder.depth >= 1 ? { width: '28px', height: '28px', borderRadius: '8px', fontSize: '10px' } : {}">夹</span>
                      <div class="aft-sb-folder-copy">
                        <div class="aft-sb-folder-name">{{ folder.name === '/' ? '根目录' : folder.name + '/' }}</div>
                        <div class="aft-sb-folder-meta">{{ folder.files.length }} 个文件 · 目录</div>
                      </div>
                    </div>
                    <!-- 文件列表（文件夹下的文件） -->
                    <div class="aft-sb-files" :style="{ paddingLeft: ((folder.depth + 1) * 14) + 'px' }">
                      <button
                        v-for="file in folder.files"
                        :key="file.id"
                        class="aft-sb-file"
                        :class="{ active: aftActiveFileId === file.id }"
                        type="button"
                        @click.stop="aftSelectFile(file, folder)"
                      >
                        <span class="aft-sb-file-icon" :class="aftFileIconClass(file.name)">{{ aftFileIconLabel(file.name) }}</span>
                        <div class="aft-sb-file-copy">
                          <div class="aft-sb-file-name">{{ file.name }}</div>
                          <div class="aft-sb-file-meta">{{ file.createdBy || 'Agent' }}</div>
                        </div>
                      </button>
                    </div>
                  </div>
                </template>
              </aside>
              <!-- 右侧内容区 -->
              <div class="aft-main">
                <!-- 面包屑 -->
                <div class="aft-breadcrumb">
                  <span class="aft-bc-seg">workspace/</span>
                  <span v-if="aftBreadcrumb" class="aft-bc-sep">/</span>
                  <span v-if="aftBreadcrumb" class="aft-bc-cur">{{ aftBreadcrumb }}</span>
                </div>
                <!-- 内容区 -->
                <div class="aft-scroll">
                  <!-- 空状态 -->
                  <div v-if="!aftActiveFolderId" class="aft-empty">
                    <div class="aft-empty-icon">📂</div>
                    <div class="aft-empty-title">选择文件或文件夹</div>
                    <div class="aft-empty-sub">点击左侧任意条目即可预览内容</div>
                  </div>
                  <!-- 文件夹视图 -->
                  <div v-else-if="!aftActiveFileId && aftActiveFolder" class="aft-folder-view">
                    <div class="aft-folder-title-row">
                      <div class="aft-folder-title">{{ aftActiveFolder.name === '/' ? '根目录' : aftActiveFolder.name + '/' }}</div>
                      <div class="aft-folder-sub">{{ aftActiveFolder.files.length }} 个文件</div>
                    </div>
                    <div>
                      <div class="aft-list-label">目录内容</div>
                      <div class="aft-file-list-card">
                        <div
                          v-for="file in aftActiveFolder.files"
                          :key="file.id"
                          class="aft-file-row"
                          :class="{ active: aftActiveFileId === file.id }"
                          @click="aftSelectFile(file, aftActiveFolder)"
                        >
                          <span class="aft-fr-icon" :class="aftFileIconClass(file.name)">{{ aftFileIconLabel(file.name) }}</span>
                          <div class="aft-fr-copy">
                            <div class="aft-fr-name">{{ file.name }}</div>
                            <div class="aft-fr-meta">{{ file.createdBy || 'Agent' }}</div>
                          </div>
                          <div class="aft-fr-actions">
                            <button v-if="file.isAgent" class="aft-fr-btn" type="button" @click.stop="openFileHistory(file)">历史</button>
                            <button class="aft-fr-btn" type="button" @click.stop="downloadFolderFile(file)">下载</button>
                            <button class="aft-fr-btn primary" type="button" @click.stop="previewFolderFile(file)">预览</button>
                          </div>
                        </div>
                        <div v-if="!aftActiveFolder.files.length" class="aft-empty-inline">当前目录下暂无文件</div>
                      </div>
                    </div>
                  </div>
                  <!-- 文件详情视图 -->
                  <div v-else-if="aftActiveFileId && aftActiveFile" class="aft-file-preview-view">
                    <div class="aft-fpv-bar">
                      <span class="aft-fpv-icon" :class="aftFileIconClass(aftActiveFile.name)">{{ aftFileIconLabel(aftActiveFile.name) }}</span>
                      <div class="aft-fpv-copy">
                        <div class="aft-fpv-name">{{ aftActiveFile.name }}</div>
                        <div class="aft-fpv-meta">{{ aftActiveFile.createdBy || 'Agent' }}</div>
                      </div>
                      <div class="aft-fpv-actions">
                        <button v-if="aftActiveFile.isAgent" class="aft-fpv-btn" type="button" @click.stop="openFileHistory(aftActiveFile)">历史</button>
                        <button class="aft-fpv-btn" type="button" @click.stop="downloadFolderFile(aftActiveFile)">下载</button>
                        <button class="aft-fpv-btn primary" type="button" @click.stop="previewFolderFile(aftActiveFile)">预览</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Artifacts tab: same as files tab, type=agent -->
          <template v-else-if="currentGroupWorkspaceTab === 'artifacts'">
            <div class="gw-files-panel">
              <!-- filter bar -->
              <div class="gw-files-filter-bar">
                <div class="gw-files-filter-pills">
                  <button
                    v-for="f in artifactFilters"
                    :key="f.key"
                    type="button"
                    class="gw-files-filter-btn"
                    :class="{ active: activeFileFilter === f.key }"
                    @click="activeFileFilter = f.key"
                  >{{ f.label }}</button>
                </div>
                <span v-if="filesTotal > 0" class="gw-files-total">共 {{ filesTotal }} 个文件</span>
              </div>
              <!-- loading -->
              <div v-if="conversationFilesLoading" class="gw-files-loading">
                <div class="gw-files-loading-dot"></div>
                <div class="gw-files-loading-dot"></div>
                <div class="gw-files-loading-dot"></div>
              </div>
              <!-- empty state -->
              <div v-else-if="filteredApiFiles.length === 0" class="gw-files-empty">
                <svg class="gw-files-empty-svg" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="20" y="30" width="80" height="70" rx="8" fill="#EEF2FF" stroke="#C7D2FE" stroke-width="2"/>
                  <rect x="30" y="20" width="60" height="70" rx="8" fill="#F5F7FF" stroke="#C7D2FE" stroke-width="2"/>
                  <path d="M45 55h30M45 67h20" stroke="#A5B4FC" stroke-width="2.5" stroke-linecap="round"/>
                  <circle cx="85" cy="85" r="18" fill="#EEF2FF" stroke="#C7D2FE" stroke-width="2"/>
                  <path d="M79 85h12M85 79v12" stroke="#A5B4FC" stroke-width="2.5" stroke-linecap="round"/>
                </svg>
                <div class="gw-files-empty-title">暂无文件</div>
                <div class="gw-files-empty-sub">在对话中发送文件，它们会出现在这里</div>
              </div>
              <!-- file list -->
              <div v-else class="gw-files-list" @scroll="onFilesScroll">
                <div
                  v-for="item in filteredApiFiles"
                  :key="item.id"
                  class="gw-file-row"
                >
                  <div class="gw-file-row-icon" :class="gwIconClass(item)">
                    <svg v-if="item.kind === 'image'" viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>
                    <svg v-else-if="item.kind === 'video'" viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/></svg>
                    <svg v-else-if="item.kind === 'audio'" viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg>
                    <svg v-else-if="item.ext === 'pdf'" viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z"/></svg>
                    <svg v-else viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/></svg>
                  </div>
                  <div class="gw-file-row-info">
                    <div class="gw-file-row-name">{{ item.title }}</div>
                    <div class="gw-file-row-meta">{{ item.senderMeta || item.meta }}</div>
                  </div>
                  <div class="gw-file-row-actions">
                    <button type="button" class="gw-file-action-btn" @click.stop="openFileHistory(item)">历史</button>
                    <button type="button" class="gw-file-action-btn" @click.stop="downloadFile(item)">下载</button>
                    <button type="button" class="gw-file-action-btn primary" @click.stop="openPreview(item)">预览</button>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Cloud tab: real VNC agents -->
          <template v-if="currentGroupWorkspaceTab === 'cloud'">

            <!-- ═══ 私聊 Agent 云电脑 ═══ -->
            <div v-if="currentSessionType === 'agent'" class="group-cloud-workspace">
              <div v-if="agentsLoading" class="group-cloud-empty">正在连接云电脑…</div>
              <div v-else-if="realAgents.length === 0" class="group-cloud-empty">
                当前会话暂无在线云电脑。<br/>Agent 启动后会自动出现在这里。
              </div>
              <div v-else class="group-cloud-simple-list">
                <section v-for="agent in realAgents" :key="agent.id" class="group-cloud-simple-item">
                  <div class="group-cloud-simple-frame">
                    <div class="group-cloud-simple-device is-large">
                      <div class="group-cloud-simple-screen">
                        <iframe v-if="agent.gatewayUrl" :key="agent.id + ':' + vncReloadKey" :src="vncUrl(agent)" class="group-cloud-vnc-iframe" frameborder="0" allow="fullscreen"></iframe>
                        <div v-else class="group-cloud-vnc-no-url">VNC 未就绪</div>
                        <div class="group-cloud-vnc-title-bar">
                          <span class="group-cloud-vnc-dot" :class="agent.status === 'running' ? 'online' : 'idle'"></span>
                          <span class="group-cloud-vnc-title-text">{{ agent.name }}</span>
                        </div>
                        <div class="group-cloud-card-actions">
                          <div class="group-cloud-actions-inner">
                            <div style="display:flex;gap:10px">
                              <button class="group-cloud-action" type="button" @click.stop="enterFullscreen(agent)">全屏查看</button>
                              <button class="group-cloud-action" type="button" @click.stop="takeoverAgent(agent)">接管电脑</button>
                            </div>
                            <div style="display:flex;gap:10px">
                              <button class="group-cloud-action secondary" type="button" @click.stop="restartOpenClaw(agent)">重启 Agent</button>
                              <button class="group-cloud-action danger" type="button" @click.stop="rescueOpenClaw(agent)">恢复出厂</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>

              <!-- 私聊思考过程 -->
              <div class="group-cloud-desktop-info-card" :class="{ 'has-thinking': isAgentThinking || thinkingMessages.length }">
                <div class="group-cloud-desktop-info-head">
                  <div class="group-cloud-desktop-info-dot" :class="{ 'is-thinking': isAgentThinking || thinkingMessages.length }"></div>
                  <span class="group-cloud-desktop-info-title">{{ (isAgentThinking || thinkingMessages.length) ? '思考过程' : '桌面内容' }}</span>
                  <span class="group-cloud-desktop-info-badge" :class="{ 'is-thinking': isAgentThinking }">{{ isAgentThinking ? '思考中…' : thinkingMessages.length ? thinkingMessages.length + ' 条记录' : '待命中' }}</span>
                </div>
                <div v-if="isAgentThinking || thinkingMessages.length" class="group-cloud-thinking-body" ref="thinkingBody" @scroll="onThinkingScroll">
                  <div v-if="agentMessagesHasMore" class="thinking-load-more" @click="loadMoreThinking">
                    {{ agentMessagesLoading ? '加载中...' : '加载更多' }}
                  </div>
                  <div v-for="msg in thinkingMessages" :key="msg.id" class="thinking-message-item">
                    <div class="thinking-message-time">{{ formatThinkingTime(msg.createdAt) }}</div>
                    <div class="thinking-message-content markdown-body" v-html="renderMd(msg.thinking)"></div>
                  </div>
                  <div v-if="isAgentThinking" class="thinking-message-item is-streaming">
                    <div class="thinking-message-time">正在思考…</div>
                    <div v-if="streamingThinkingText" class="thinking-message-content markdown-body" v-html="renderMd(streamingThinkingText)"></div>
                    <div v-else class="thinking-message-content thinking-dots">
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                </div>
                <div v-else class="group-cloud-desktop-info-items">
                  <div class="group-cloud-desktop-info-item">
                    <div class="group-cloud-desktop-info-icon">🌐</div>
                    <div class="group-cloud-desktop-info-copy">
                      <div class="group-cloud-desktop-info-item-title">Google Chrome</div>
                      <div class="group-cloud-desktop-info-item-desc">准备发起搜索任务，等待用户输入问题。</div>
                    </div>
                  </div>
                  <div class="group-cloud-desktop-info-item">
                    <div class="group-cloud-desktop-info-icon">📁</div>
                    <div class="group-cloud-desktop-info-copy">
                      <div class="group-cloud-desktop-info-item-title">项目文件</div>
                      <div class="group-cloud-desktop-info-item-desc">当前项目原型与会话草稿已在桌面待命。</div>
                    </div>
                  </div>
                  <div class="group-cloud-desktop-info-item">
                    <div class="group-cloud-desktop-info-icon">📥</div>
                    <div class="group-cloud-desktop-info-copy">
                      <div class="group-cloud-desktop-info-item-title">下载目录</div>
                      <div class="group-cloud-desktop-info-item-desc">等待新的网页资料、图片或附件进入工作流。</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ═══ 群聊云电脑（每个 agent 带独立思考步骤面板）═══ -->
            <div v-else class="group-cloud-workspace group-cloud-workspace-group">
              <div v-if="agentsLoading" class="group-cloud-empty">正在连接云电脑…</div>
              <div v-else-if="realAgents.length === 0" class="group-cloud-empty">
                当前会话暂无在线云电脑。<br/>Agent 启动后会自动出现在这里。
              </div>
              <div v-else class="group-cloud-simple-list">
                <section v-for="agent in realAgents" :key="agent.id" class="group-cloud-simple-item">
                  <!-- 标题：xxx的电脑 + 所属人 chip -->
                  <div class="group-agent-card-title">
                    <span class="group-agent-card-title-text">{{ agent.name }}{{ agent.roleName ? '-' + agent.roleName : '' }}的电脑</span>
                    <span v-if="agentOwner(agent)" class="group-agent-card-owner-chip">
                      <svg viewBox="0 0 24 24" fill="none" class="group-agent-card-owner-icon"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="1.7"/></svg>
                      <span>{{ agentOwner(agent) }}</span>
                    </span>
                  </div>
                  <div class="group-cloud-simple-frame">
                    <div class="group-cloud-simple-device is-large">
                      <div class="group-cloud-simple-screen">
                        <iframe v-if="agent.gatewayUrl" :key="agent.id + ':' + vncReloadKey" :src="vncUrl(agent)" class="group-cloud-vnc-iframe" frameborder="0" allow="fullscreen"></iframe>
                        <div v-else class="group-cloud-vnc-no-url">VNC 未就绪</div>
                        <div class="group-cloud-vnc-title-bar">
                          <span class="group-cloud-vnc-dot" :class="agent.status === 'running' ? 'online' : 'idle'"></span>
                          <span class="group-cloud-vnc-title-text">{{ agent.name }}</span>
                        </div>
                        <div class="group-cloud-card-actions">
                          <div class="group-cloud-actions-inner">
                            <div style="display:flex;gap:10px">
                              <button class="group-cloud-action" type="button" @click.stop="enterFullscreen(agent)">全屏查看</button>
                              <button class="group-cloud-action" type="button" @click.stop="takeoverAgent(agent)">接管电脑</button>
                            </div>
                            <div style="display:flex;gap:10px">
                              <button class="group-cloud-action secondary" type="button" @click.stop="restartOpenClaw(agent)">重启 Agent</button>
                              <button class="group-cloud-action danger" type="button" @click.stop="rescueOpenClaw(agent)">恢复出厂</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- 任务计划面板（group_plan）：无数据时不显示 -->
                  <div class="group-agent-thinking-panel" v-if="getAgentGroupPlan(agent.id) && getAgentGroupPlan(agent.id).metadata.steps && getAgentGroupPlan(agent.id).metadata.steps.length">
                    <div class="group-agent-thinking-header">
                      <span class="group-agent-thinking-dot" :class="{ streaming: getAgentGroupPlan(agent.id).metadata.status === 'running' }"></span>
                      <span class="group-agent-thinking-status">{{ getAgentGroupPlan(agent.id).metadata.status === 'running' ? '正在思考与执行中' : '思考完成' }}</span>
                    </div>
                    <div class="group-agent-thinking-steps" :class="{ expanded: agentThinkingExpanded[agent.id] }">
                      <div v-for="step in getAgentPlanStepsVisible(agent.id)" :key="step.index" class="group-agent-step-row">
                        <span class="group-agent-step-dot" :class="step.status"></span>
                        <span class="group-agent-step-text">{{ step.text }}</span>
                        <span class="group-agent-step-badge" :class="step.status">{{ step.status === 'done' ? '已完成' : step.status === 'running' ? '执行中' : '思考中' }}</span>
                      </div>
                    </div>
                    <div v-if="getAgentGroupPlan(agent.id).metadata.steps.length > 3" class="group-agent-step-more" @click="toggleAgentThinking(agent.id)">
                      {{ agentThinkingExpanded[agent.id] ? '收起 ▲' : '展开全部 ' + getAgentGroupPlan(agent.id).metadata.steps.length + ' 步 ▼' }}
                    </div>
                  </div>
                </section>
              </div>
            </div>

          </template>


          <!-- Changes tab: 暂无数据 -->
          <template v-else-if="currentGroupWorkspaceTab === 'changes'">
            <div class="gw-under-dev">
              <svg viewBox="0 0 48 48" fill="none" width="40" height="40"><path d="M12 24h24M12 16h16M12 32h20" stroke="#c8d4e3" stroke-width="2.5" stroke-linecap="round"/><circle cx="36" cy="16" r="4" stroke="#c8d4e3" stroke-width="2.5" fill="none"/><circle cx="28" cy="32" r="4" stroke="#c8d4e3" stroke-width="2.5" fill="none"/></svg>
              <div class="gw-under-dev-title">暂无变更</div>
              <div class="gw-under-dev-sub">本次会话的文件修改记录将在这里展示</div>
            </div>
          </template>

          <!-- Preview tab -->
          <template v-else-if="currentGroupWorkspaceTab === 'preview'">
            <!-- Doc preview -->
            <template v-if="currentGroupWorkspacePreview && currentGroupWorkspacePreview.previewMode === 'doc'">
              <div class="group-workspace-doc">
                <div>
                  <div class="group-workspace-doc-title">{{ currentGroupWorkspacePreview.title }}</div>
                  <div class="group-workspace-doc-meta">{{ currentGroupWorkspacePreview.meta || '文件预览' }}</div>
                </div>
                <section class="group-workspace-doc-card">
                  <h4>摘要</h4>
                  <p>{{ currentGroupWorkspacePreview.summary || currentGroupWorkspacePreview.desc || '暂无更多说明。' }}</p>
                </section>
                <section class="group-workspace-doc-card">
                  <h4>预览说明</h4>
                  <ul>
                    <li>支持在群聊右侧工作台中直接查看当前文件。</li>
                    <li>可从「全部文件」中切换其他附件或交付物。</li>
                    <li>点击会话中的文件卡片也会自动跳转到「预览」。</li>
                  </ul>
                </section>
              </div>
            </template>
            <!-- Code preview -->
            <template v-else-if="currentGroupWorkspacePreview && currentGroupWorkspacePreview.previewMode === 'code'">
              <div class="group-workspace-code">
                <div v-for="(line, idx) in codeLines" :key="idx" class="group-workspace-code-row" :class="{ current: idx === 2 }">
                  <div class="group-workspace-code-no">{{ idx + 1 }}</div>
                  <div class="group-workspace-code-text">{{ line || ' ' }}</div>
                </div>
              </div>
            </template>
            <!-- Diff preview -->
            <template v-else-if="currentGroupWorkspacePreview && currentGroupWorkspacePreview.previewMode === 'diff'">
              <div class="group-workspace-diff">
                <div v-for="(line, idx) in (currentGroupWorkspacePreview.diffLines || [])" :key="idx" class="group-workspace-diff-row" :class="line.type">
                  {{ line.text }}
                </div>
              </div>
            </template>
            <!-- File preview (from chat file click) -->
            <template v-else-if="currentGroupWorkspacePreview && currentGroupWorkspacePreview.previewMode === 'file'">
              <div class="group-workspace-file-preview">
                <div class="group-workspace-file-preview-head">
                  <div class="group-workspace-file-preview-name">{{ currentGroupWorkspacePreview.fileName }}</div>
                  <div class="gw-preview-head-actions">
                    <button type="button" class="gw-fullscreen-btn" @click="fullscreenPreview = currentGroupWorkspacePreview" title="全屏预览">
                      <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M16 21h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    </button>
                    <button v-if="currentGroupWorkspacePreview.url" type="button" class="group-workspace-file-preview-download" @click="downloadFile(currentGroupWorkspacePreview)">↓ 下载</button>
                  </div>
                </div>
                <!-- 图片 -->
                <img v-if="currentGroupWorkspacePreview.kind === 'image' && currentGroupWorkspacePreview.ext === 'svg' && svgDataUrl" :src="svgDataUrl" class="group-workspace-file-preview-img" />
                <div v-else-if="currentGroupWorkspacePreview.kind === 'image' && currentGroupWorkspacePreview.ext === 'svg' && !svgDataUrl" class="group-workspace-file-preview-loading">加载 SVG 中…</div>
                <img v-else-if="currentGroupWorkspacePreview.kind === 'image'" :src="currentGroupWorkspacePreview.url" class="group-workspace-file-preview-img" :alt="currentGroupWorkspacePreview.fileName" />
                <!-- 视频 -->
                <video v-else-if="currentGroupWorkspacePreview.kind === 'video'" :src="currentGroupWorkspacePreview.url" class="group-workspace-file-preview-video" controls preload="auto" playsinline></video>
                <!-- 音频 -->
                <div v-else-if="currentGroupWorkspacePreview.kind === 'audio'" class="ws-audio-preview">
                  <div class="ws-audio-preview-cover">
                    <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="80" height="80">
                      <circle cx="40" cy="40" r="40" fill="rgba(99,140,255,0.12)"/>
                      <circle cx="40" cy="40" r="28" fill="rgba(99,140,255,0.18)"/>
                      <path d="M33 28v24l20-12z" fill="#7ba4ff"/>
                    </svg>
                  </div>
                  <div class="ws-audio-preview-name">{{ currentGroupWorkspacePreview.fileName }}</div>
                  <div class="ws-audio-preview-size">{{ currentGroupWorkspacePreview.fileSizeStr }}</div>
                  <!-- hidden audio element -->
                  <audio
                    ref="wsAudioEl"
                    :src="currentGroupWorkspacePreview.url"
                    preload="metadata"
                    @timeupdate="e => { wsAudio.current = e.target.currentTime; wsAudio.duration = e.target.duration || wsAudio.duration }"
                    @loadedmetadata="e => { wsAudio.duration = e.target.duration }"
                    @ended="() => { wsAudio.playing = false; wsAudio.current = 0; $refs.wsAudioEl && ($refs.wsAudioEl.currentTime = 0) }"
                  ></audio>
                  <!-- custom player controls -->
                  <div class="ws-audio-controls">
                    <button class="ws-audio-play-btn" type="button" @click="wsTogglePlay">
                      <svg v-if="wsAudio.playing" viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                      <svg v-else viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M8 5v14l11-7z"/></svg>
                    </button>
                    <div class="ws-audio-slider-wrap" @click="wsSeek($event)">
                      <div class="ws-audio-slider-track">
                        <div class="ws-audio-slider-fill" :style="{ width: wsAudioProgress + '%' }"></div>
                        <div class="ws-audio-slider-thumb" :style="{ left: wsAudioProgress + '%' }"></div>
                      </div>
                    </div>
                    <span class="ws-audio-time">{{ wsAudioTimeStr }}</span>
                  </div>
                  <!-- bars decoration -->
                  <div class="ws-audio-bars-deco">
                    <span v-for="n in 40" :key="n" class="ws-audio-deco-bar" :class="{ lit: (n / 40 * 100) <= wsAudioProgress }"></span>
                  </div>
                  <a v-if="currentGroupWorkspacePreview.url" :href="currentGroupWorkspacePreview.url" :download="currentGroupWorkspacePreview.fileName" class="ws-audio-download-btn" @click.prevent="downloadFile(currentGroupWorkspacePreview)">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    下载文件
                  </a>
                </div>
                <!-- PDF -->
                <iframe v-else-if="currentGroupWorkspacePreview.mimeType === 'application/pdf' || currentGroupWorkspacePreview.ext === 'pdf'" :src="currentGroupWorkspacePreview.url" class="group-workspace-file-preview-iframe" frameborder="0"></iframe>
                <!-- HTML 文件：用 srcdoc 渲染已拉取内容，避免 Content-Disposition:attachment 触发下载 -->
                <div v-else-if="isHtmlPreview" class="group-workspace-file-preview-iframe-wrap">
                  <iframe
                    v-if="fileTextContent"
                    :srcdoc="fileTextContent"
                    class="group-workspace-file-preview-iframe"
                    frameborder="0"
                    sandbox="allow-scripts allow-same-origin"
                  ></iframe>
                  <div v-else class="group-workspace-file-preview-loading">加载中…</div>
                </div>
                <!-- Markdown 文件：渲染效果 -->
                <div v-else-if="isMarkdownPreview" class="group-workspace-file-preview-code-wrap" style="background:#fff;">
                  <div v-if="fileTextContent" class="group-workspace-md-render md-content" v-html="renderMd(fileTextContent)"></div>
                  <div v-else class="group-workspace-file-preview-loading">加载中…</div>
                </div>
                <!-- 代码 / 文本文件 -->
                <div v-else-if="isTextPreview" class="group-workspace-file-preview-code-wrap">
                  <template v-if="fileTextContent">
                    <div class="code-block-header">
                      <span class="code-block-lang">{{ (currentGroupWorkspacePreview.ext || 'txt').toUpperCase() }}</span>
                    </div>
                    <div class="code-block-body">
                      <div class="code-line-numbers" aria-hidden="true">
                        <span v-for="n in codeLineCount" :key="n">{{ n }}</span>
                      </div>
                      <pre class="code-block-pre"><code :class="codeClass" v-html="highlightedCode"></code></pre>
                    </div>
                  </template>
                  <div v-else class="group-workspace-file-preview-loading">加载中…</div>
                </div>
                <!-- Excel 预览（SheetJS 动态解析） -->
                <div v-else-if="isSpreadsheetPreview" class="group-workspace-file-preview-sheet-wrap">
                  <div v-if="sheetLoading" class="group-workspace-file-preview-loading">正在解析文件…</div>
                  <div v-else-if="sheetError" class="group-workspace-file-preview-doc">
                    <div class="group-workspace-file-preview-doc-icon">{{ currentGroupWorkspacePreview.ext ? currentGroupWorkspacePreview.ext.toUpperCase().slice(0,4) : 'XLS' }}</div>
                    <div class="group-workspace-file-preview-doc-name">{{ currentGroupWorkspacePreview.fileName }}</div>
                    <div class="group-workspace-file-preview-doc-size" style="color:#e55">{{ sheetError }}</div>
                    <a v-if="currentGroupWorkspacePreview.url" :href="currentGroupWorkspacePreview.url" download class="group-workspace-file-preview-doc-download">下载文件</a>
                  </div>
                  <div v-else-if="sheetHtml" class="group-workspace-file-preview-sheet-body">
                    <div v-if="sheetNames.length > 1" class="group-workspace-sheet-tabs">
                      <button
                        v-for="name in sheetNames"
                        :key="name"
                        class="group-workspace-sheet-tab"
                        :class="{ active: activeSheetName === name }"
                        type="button"
                        @click="switchSheet(name)"
                      >{{ name }}</button>
                    </div>
                    <div class="group-workspace-sheet-table-wrap" v-html="sheetHtml"></div>
                  </div>
                </div>
                <!-- Office (ppt/pptx/doc/docx) 等"国内不友好"的类型不预览，走下面的 fallback 下载 -->
                <!-- 其他文件 -->
                <div v-else class="group-workspace-file-preview-doc">
                  <div class="group-workspace-file-preview-doc-icon">{{ currentGroupWorkspacePreview.ext ? currentGroupWorkspacePreview.ext.toUpperCase().slice(0,4) : '文' }}</div>
                  <div class="group-workspace-file-preview-doc-name">{{ currentGroupWorkspacePreview.fileName }}</div>
                  <div class="group-workspace-file-preview-doc-size">{{ currentGroupWorkspacePreview.fileSizeStr }}</div>
                  <a v-if="currentGroupWorkspacePreview.url" :href="currentGroupWorkspacePreview.url" download class="group-workspace-file-preview-doc-download">下载文件</a>
                </div>
              </div>
            </template>
            <!-- Empty state -->
            <template v-else>
              <div class="group-workspace-preview-empty">
                <div class="group-workspace-preview-empty-icon">👁</div>
                <div class="group-workspace-preview-empty-text">暂无预览内容</div>
                <div class="group-workspace-preview-empty-sub">点击左侧文件或会话内文件开始预览</div>
              </div>
            </template>
          </template>

          <!-- Artifact overview: stat grid + file sections -->
          <template v-else-if="selectedItem && selectedItem.previewMode === 'artifact-overview'">
            <div class="group-workspace-doc">
              <div>
                <div class="group-workspace-doc-title">交付物</div>
                <div class="group-workspace-doc-meta">只展示当前会话中 AI 生成的成果与文件变更，按会话/任务聚焦展示。</div>
              </div>
              <section class="group-workspace-doc-card">
                <h4>文件变更</h4>
                <div class="group-workspace-stat-grid">
                  <div class="group-workspace-stat-card new">
                    <div class="group-workspace-stat-value">{{ (selectedItem.changeEntries.created || []).length }}</div>
                    <div class="group-workspace-stat-label">新建</div>
                  </div>
                  <div class="group-workspace-stat-card modified">
                    <div class="group-workspace-stat-value">{{ (selectedItem.changeEntries.modified || []).length }}</div>
                    <div class="group-workspace-stat-label">修改</div>
                  </div>
                  <div class="group-workspace-stat-card deleted">
                    <div class="group-workspace-stat-value">{{ (selectedItem.changeEntries.deleted || []).length }}</div>
                    <div class="group-workspace-stat-label">删除</div>
                  </div>
                </div>
              </section>
              <section v-if="(selectedItem.changeEntries.created || []).length" class="group-workspace-doc-card">
                <h4>📄 新建</h4>
                <div class="group-workspace-file-rows">
                  <button v-for="f in selectedItem.changeEntries.created" :key="f.id" class="group-workspace-file-row" type="button" @click="openPreview(f)">
                    <span class="group-workspace-file-row-main">
                      <span class="group-workspace-file-row-icon">{{ f.icon || '文' }}</span>
                      <span class="group-workspace-file-row-copy">
                        <span class="group-workspace-file-row-title">{{ f.title }}</span>
                        <span class="group-workspace-file-row-meta">{{ f.meta || '新建' }}</span>
                      </span>
                    </span>
                    <span class="group-workspace-file-row-arrow">预览</span>
                  </button>
                </div>
              </section>
              <section v-else class="group-workspace-doc-card">
                <h4>📄 新建</h4>
                <div class="group-workspace-empty-inline">当前没有新建文件。</div>
              </section>
              <section v-if="(selectedItem.changeEntries.modified || []).length" class="group-workspace-doc-card">
                <h4>✏️ 修改</h4>
                <div class="group-workspace-file-rows">
                  <button v-for="f in selectedItem.changeEntries.modified" :key="f.id" class="group-workspace-file-row" type="button" @click="openPreview(f)">
                    <span class="group-workspace-file-row-main">
                      <span class="group-workspace-file-row-icon">{{ f.icon || '文' }}</span>
                      <span class="group-workspace-file-row-copy">
                        <span class="group-workspace-file-row-title">{{ f.title }}</span>
                        <span class="group-workspace-file-row-meta">{{ f.meta || '修改' }}</span>
                      </span>
                    </span>
                    <span class="group-workspace-file-row-arrow">预览</span>
                  </button>
                </div>
              </section>
              <section v-else class="group-workspace-doc-card">
                <h4>✏️ 修改</h4>
                <div class="group-workspace-empty-inline">当前没有修改文件。</div>
              </section>
            </div>
          </template>

          <!-- Artifact list -->
          <template v-else-if="selectedItem && selectedItem.previewMode === 'artifact-list'">
            <div class="group-workspace-doc">
              <div>
                <div class="group-workspace-doc-title">{{ selectedItem.title }}</div>
                <div class="group-workspace-doc-meta">{{ selectedItem.meta || '会话交付物' }}</div>
              </div>
              <section class="group-workspace-doc-card">
                <h4>可预览文件</h4>
                <div v-if="(selectedItem.files || []).length" class="group-workspace-file-rows">
                  <button v-for="f in selectedItem.files" :key="f.id" class="group-workspace-file-row" type="button" @click="openPreview(f)">
                    <span class="group-workspace-file-row-main">
                      <span class="group-workspace-file-row-icon">{{ f.icon || '文' }}</span>
                      <span class="group-workspace-file-row-copy">
                        <span class="group-workspace-file-row-title">{{ f.title }}</span>
                        <span class="group-workspace-file-row-meta">{{ f.meta }}</span>
                      </span>
                    </span>
                    <span class="group-workspace-file-row-arrow">预览</span>
                  </button>
                </div>
                <div v-else class="group-workspace-empty-inline">当前没有可展示的成果文件。</div>
              </section>
            </div>
          </template>

          <!-- Folder -->
          <template v-else-if="selectedItem && selectedItem.previewMode === 'folder'">
            <div class="group-workspace-doc">
              <div>
                <div class="group-workspace-doc-title">{{ selectedItem.title }}</div>
                <div class="group-workspace-doc-meta">{{ selectedItem.meta || '目录' }}</div>
              </div>
              <section class="group-workspace-doc-card">
                <h4>目录内容</h4>
                <!-- 接口真实文件列表 -->
                <div v-if="(selectedItem.folderFiles || []).length" class="group-workspace-file-rows">
                  <div v-for="f in selectedItem.folderFiles" :key="f.id" class="group-workspace-file-row gw-folder-file-real-row">
                    <span class="group-workspace-file-row-main">
                      <span class="group-workspace-file-row-icon">
                        <svg v-if="folderFileIcon(f.name) === 'image'" viewBox="0 0 24 24" fill="currentColor" width="16" height="16" style="color:#4f8cf4"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>
                        <svg v-else-if="folderFileIcon(f.name) === 'pdf'" viewBox="0 0 24 24" fill="currentColor" width="16" height="16" style="color:#e53935"><path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>
                        <svg v-else-if="['ppt','pptx'].includes((f.name||'').split('.').pop().toLowerCase())" viewBox="0 0 24 24" fill="currentColor" width="16" height="16" style="color:#f5a623"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/></svg>
                        <svg v-else-if="folderFileIcon(f.name) === 'code'" viewBox="0 0 24 24" fill="currentColor" width="16" height="16" style="color:#7b61ff"><path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/></svg>
                        <svg v-else viewBox="0 0 24 24" fill="currentColor" width="16" height="16" style="color:#8d9bab"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/></svg>
                      </span>
                      <span class="group-workspace-file-row-copy">
                        <span class="group-workspace-file-row-title">{{ f.name }}</span>
                        <span class="group-workspace-file-row-meta">{{ f.createdBy }}</span>
                      </span>
                    </span>
                    <span class="gw-folder-real-actions">
                      <button type="button" class="gw-file-action-btn" @click.stop="downloadFolderFile(f)">下载</button>
                      <button type="button" class="gw-file-action-btn primary" @click.stop="previewFolderFile(f)">预览</button>
                    </span>
                  </div>
                </div>
                <!-- 旧版 synthetic children 兜底 -->
                <div v-else-if="(selectedItem.children || []).length" class="group-workspace-file-rows">
                  <button v-for="f in selectedItem.children" :key="f.id" class="group-workspace-file-row" type="button" @click="openPreview(f)">
                    <span class="group-workspace-file-row-main">
                      <span class="group-workspace-file-row-icon">{{ f.icon || '文' }}</span>
                      <span class="group-workspace-file-row-copy">
                        <span class="group-workspace-file-row-title">{{ f.title }}</span>
                        <span class="group-workspace-file-row-meta">{{ f.meta }}</span>
                      </span>
                    </span>
                    <span class="group-workspace-file-row-arrow">预览</span>
                  </button>
                </div>
                <div v-else class="group-workspace-empty-inline">当前目录下暂无文件。</div>
              </section>
            </div>
          </template>

          <!-- Change overview -->
          <template v-else-if="selectedItem && selectedItem.previewMode === 'change-overview'">
            <div class="group-workspace-doc">
              <div>
                <div class="group-workspace-doc-title">本次会话的文件修改记录</div>
                <div class="group-workspace-doc-meta">{{ selectedItem.summary || '追踪本次会话的文件修改记录，便于审计与回滚。' }}</div>
              </div>
              <section class="group-workspace-doc-card">
                <h4>快速审计</h4>
                <div class="group-workspace-file-rows">
                  <button
                    v-for="f in [...(selectedItem.changeEntries.created || []), ...(selectedItem.changeEntries.modified || []), ...(selectedItem.changeEntries.deleted || [])]"
                    :key="f.id"
                    class="group-workspace-file-row"
                    type="button"
                    @click="openPreview(f)"
                  >
                    <span class="group-workspace-file-row-main">
                      <span class="group-workspace-file-row-icon">{{ f.icon || '文' }}</span>
                      <span class="group-workspace-file-row-copy">
                        <span class="group-workspace-file-row-title">{{ f.title }}</span>
                        <span class="group-workspace-file-row-meta">{{ f.changeLabel || f.meta }}</span>
                      </span>
                    </span>
                    <span class="group-workspace-file-row-arrow">预览</span>
                  </button>
                </div>
              </section>
            </div>
          </template>

          <!-- Empty / other -->
          <template v-else>
            <div class="group-workspace-browser">
              <div class="group-workspace-browser-top">
                <div class="group-workspace-browser-nav">‹ ›</div>
                <div class="group-workspace-browser-address">搜索或输入网址</div>
              </div>
              <div class="group-workspace-browser-body">暂无预览内容，点击左侧文件或会话内文件开始预览</div>
            </div>
          </template>

        </div>
      </section>
    </div>

  <!-- 全屏预览弹窗（在根 div 内） -->
  <div v-if="fullscreenPreview" class="gw-fullscreen-modal" @click.self="onModalBackdropClick">
    <div
      v-if="previewCtx.open"
      class="gw-preview-ctx-menu"
      :style="{ left: previewCtx.x + 'px', top: previewCtx.y + 'px' }"
      @click.stop
      @contextmenu.prevent.stop
    >
      <button type="button" class="gw-preview-ctx-item" @click="doCopyPreviewImage">复制图片</button>
      <button type="button" class="gw-preview-ctx-item" @click="doDownloadPreview">下载</button>
      <button type="button" class="gw-preview-ctx-item gw-preview-ctx-item--danger" @click="doClosePreview">关闭</button>
    </div>
    <div class="gw-fullscreen-modal-inner">
      <div class="gw-fullscreen-modal-head">
        <div class="gw-fullscreen-modal-name">{{ fullscreenPreview.fileName }}</div>
        <div class="gw-fullscreen-modal-actions">
          <button v-if="fullscreenPreview.url" type="button" class="gw-fullscreen-action-btn" @click="downloadFile(fullscreenPreview)">↓ 下载</button>
          <button type="button" class="gw-fullscreen-close" @click="closeFullscreen">✕</button>
        </div>
      </div>
      <div class="gw-fullscreen-modal-body" @wheel="onPreviewWheel" @mousemove="onPreviewMouseMove" @mouseup="onPreviewMouseUp" @mouseleave="onPreviewMouseUp">
        <!-- 图片 -->
        <img v-if="fullscreenPreview.kind === 'image' && fullscreenPreview.ext === 'svg' && svgDataUrl" :src="svgDataUrl" class="gw-fullscreen-img" :style="imgZoomStyle" draggable="false" @mousedown="onPreviewMouseDown" @dblclick="onPreviewDblClick" @contextmenu.prevent.stop="onPreviewContextMenu" />
        <img v-else-if="fullscreenPreview.kind === 'image'" :src="fullscreenPreview.url" class="gw-fullscreen-img" :alt="fullscreenPreview.fileName" :style="imgZoomStyle" draggable="false" @mousedown="onPreviewMouseDown" @dblclick="onPreviewDblClick" @contextmenu.prevent.stop="onPreviewContextMenu" />
        <!-- 视频 -->
        <video v-else-if="fullscreenPreview.kind === 'video'" :src="fullscreenPreview.url" class="gw-fullscreen-video" controls autoplay playsinline></video>
        <!-- 音频 -->
        <audio v-else-if="fullscreenPreview.kind === 'audio'" :src="fullscreenPreview.url" controls class="gw-fullscreen-audio"></audio>
        <!-- PDF：用 iframe 可内嵌预览 -->
        <iframe v-else-if="fullscreenPreview.mimeType === 'application/pdf' || fullscreenPreview.ext === 'pdf'" :src="fullscreenPreview.url" class="gw-fullscreen-iframe" frameborder="0"></iframe>
        <!-- Office (ppt/pptx/doc/docx) 等"国内不友好"的类型不预览，走下面的 fallback 下载 -->

        <!-- Excel/spreadsheet：复用右侧预览面板 SheetJS 已渲染好的 HTML + sheet tabs -->
        <div v-else-if="fullscreenIsSpreadsheet" class="gw-fullscreen-sheet-wrap">
          <div v-if="sheetLoading" class="gw-fullscreen-loading">正在解析文件…</div>
          <div v-else-if="sheetError" class="gw-fullscreen-unsupported">{{ sheetError }}</div>
          <div v-else-if="sheetHtml" class="gw-fullscreen-sheet-body">
            <div v-if="sheetNames.length > 1" class="group-workspace-sheet-tabs">
              <button
                v-for="name in sheetNames"
                :key="name"
                class="group-workspace-sheet-tab"
                :class="{ active: activeSheetName === name }"
                type="button"
                @click="switchSheet(name)"
              >{{ name }}</button>
            </div>
            <div class="group-workspace-sheet-table-wrap" v-html="sheetHtml"></div>
          </div>
          <div v-else class="gw-fullscreen-loading">加载中…</div>
        </div>
        <!-- HTML：srcdoc 避免 Content-Disposition:attachment 触发下载 -->
        <iframe v-else-if="(fullscreenPreview.ext === 'html' || fullscreenPreview.ext === 'htm') && fullscreenTextContent" :srcdoc="fullscreenTextContent" class="gw-fullscreen-iframe" frameborder="0" sandbox="allow-scripts allow-same-origin"></iframe>
        <div v-else-if="fullscreenPreview.ext === 'html' || fullscreenPreview.ext === 'htm'" class="gw-fullscreen-loading">加载中…</div>
        <!-- Markdown 全屏渲染 -->
        <div v-else-if="fullscreenIsMarkdown && fullscreenPreview.url" class="gw-fullscreen-md-wrap">
          <div v-if="fullscreenTextContent" class="gw-fullscreen-md-body md-content" v-html="renderMd(fullscreenTextContent)"></div>
          <div v-else class="gw-fullscreen-md-loading">加载中…</div>
        </div>
        <!-- 文本/代码：用已拉取的 fileTextContent 渲染，避免触发下载 -->
        <div v-else-if="fullscreenIsText && fullscreenPreview.url" class="gw-fullscreen-code-wrap">
          <div v-if="fullscreenTextContent" class="code-block-body" style="height:100%;overflow:auto;display:flex;flex-direction:column">
            <div class="code-block-header" style="flex-shrink:0">
              <span class="code-block-lang">{{ (fullscreenPreview.ext || 'txt').toUpperCase() }}</span>
            </div>
            <div style="flex:1;min-height:0;overflow:auto;display:flex">
              <div class="code-line-numbers" aria-hidden="true" style="flex-shrink:0">
                <span v-for="n in fullscreenLineCount" :key="n">{{ n }}</span>
              </div>
              <pre class="code-block-pre" style="flex:1;margin:0"><code v-html="fullscreenHighlighted"></code></pre>
            </div>
          </div>
          <div v-else class="gw-fullscreen-loading">加载中…</div>
        </div>
        <!-- 不支持的类型：文件名 + 下载 -->
        <div v-else class="gw-fullscreen-unsupported">
          <div class="gw-fullscreen-unsupported-icon">{{ fullscreenPreview.ext ? fullscreenPreview.ext.toUpperCase().slice(0,5) : '文件' }}</div>
          <div class="gw-fullscreen-unsupported-name">{{ fullscreenPreview.fileName }}</div>
          <button v-if="fullscreenPreview.url" type="button" class="gw-fullscreen-unsupported-dl" @click="downloadFile(fullscreenPreview)">↓ 下载文件</button>
        </div>
      </div>
      <!-- 图片左右切换（仅图片且该话题图片 >1 张）：贴在面板内侧 -->
      <template v-if="galleryActive">
        <button type="button" class="gw-gallery-arrow gw-gallery-prev" :disabled="!canPrevImage" @click.stop="prevImage" title="上一张（←）">
          <svg viewBox="0 0 24 24" fill="none" width="22" height="22"><path d="M15 5l-7 7 7 7" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <button type="button" class="gw-gallery-arrow gw-gallery-next" :disabled="!canNextImage" @click.stop="nextImage" title="下一张（→）">
          <svg viewBox="0 0 24 24" fill="none" width="22" height="22"><path d="M9 5l7 7-7 7" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <div class="gw-gallery-counter">{{ galleryCounter }}</div>
      </template>
    </div>
  </div>

  <!-- 云电脑 Toast -->
  <div v-if="cloudToast.show" class="cloud-toast" :class="'cloud-toast-' + cloudToast.type">{{ cloudToast.msg }}</div>

  <!-- 云电脑确认弹窗 -->
  <div v-if="cloudConfirm.show" class="cloud-confirm-overlay" @click.self="resolveCloudConfirm(false)">
    <div class="cloud-confirm-panel">
      <div class="cloud-confirm-icon">⚠</div>
      <div class="cloud-confirm-msg">{{ cloudConfirm.msg }}</div>
      <div class="cloud-confirm-actions">
        <button type="button" class="cloud-confirm-btn cancel" @click="resolveCloudConfirm(false)">取消</button>
        <button type="button" class="cloud-confirm-btn confirm" @click="resolveCloudConfirm(true)">确定</button>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { api } from '../utils/api'
import { renderMarkdown } from '../utils/markdown'
import { copyImageToClipboard } from '../utils/copyImage'

const TAB_META = {
  cloud:     { title: '云电脑',   sub: '参考 Agent 工作区电脑做原型展示，先呈现桌面效果，后续可直接切到接口渲染。' },
  artifacts: { title: '交付物',     sub: '聚焦当前会话中 AI 生成的成果文件，默认展示文件变更。' },
  files:     { title: '全部文件', sub: '浏览当前工作区的完整文件树，像传统 IDE 一样管理项目文件。' },
  changes:   { title: '变更',     sub: '追踪本次会话的文件修改记录，便于审计与回滚。' },
  preview:   { title: '预览',     sub: '点击文件后在这里实时预览 HTML、图片、文档等内容。' }
}

function cloudStatusClass(status) {
  if (status === '在线') return 'cloud-online'
  if (status === '空闲') return 'cloud-idle'
  return 'cloud-busy'
}

const FILE_TYPE_ICON = {
  image: '🖼️', video: '🎬', audio: '🎵',
  document: '📄', markdown: '📝', file: '📎'
}

function formatFileSize(bytes) {
  if (!bytes || bytes <= 0) return ''
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function extractFilenameFromUrl(url) {
  if (!url) return ''
  try {
    const pathname = new URL(url).pathname
    const parts = pathname.split('/')
    const last = parts[parts.length - 1] || ''
    // strip timestamp prefix like 20260326042134_c23eda30.txt -> c23eda30.txt
    const m = last.match(/^\d{14}_(.+)$/)
    return m ? m[1] : last
  } catch { return '' }
}

function mapApiFileToItem(f, level) {
  // Real API fields: fileName, fileType, fileUrl, fileSize, mimeType, createdAt
  const rawName = f.fileName || f.name || ''
  const fileName = (rawName && rawName !== 'unnamed_file')
    ? rawName
    : (extractFilenameFromUrl(f.fileUrl || f.url) || '未命名文件')
  const fileUrl = f.fileUrl || f.url || ''
  const fileType = f.fileType || f.type || ''
  const fileSize = f.fileSize || f.size || 0
  const ext = fileName.includes('.') ? fileName.split('.').pop().toLowerCase() : ''
  const kind = fileType === 'image' ? 'image'
    : fileType === 'video' ? 'video'
    : fileType === 'audio' ? 'audio'
    : ext || fileType
  // Format date
  const createdAt = f.createdAt || f.created_at || ''
  let dateStr = ''
  if (createdAt) {
    try {
      const d = new Date(createdAt)
      dateStr = d.toLocaleDateString('zh-CN', { month: 'numeric', day: 'numeric' })
        + ' ' + d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })
    } catch (e) { console.warn('[workspace] date parse failed:', e.message) }
  }
  const sizeStr = formatFileSize(fileSize)
  const typeLabel = ext ? ext.toUpperCase() : fileType.toUpperCase()
  const metaParts = [dateStr, sizeStr, typeLabel].filter(Boolean)
  return {
    id: f.id,
    title: fileName,
    name: fileName,
    icon: FILE_TYPE_ICON[fileType] || '📎',
    meta: metaParts.join(' · '),
    senderMeta: metaParts.join(' · '),
    tag: '',
    tagClass: '',
    level: level || 0,
    opensPreview: true,
    previewMode: 'file',
    url: fileUrl,
    fileName,
    fileSizeStr: sizeStr,
    mimeType: f.mimeType || '',
    ext,
    kind,
    createdAt,
    isAgent: f.createdByType === 'agent' || f.senderType === 'agent'
  }
}

const FOLDER_CONFIG = {
  docs:   { title: 'docs/',   types: ['document', 'markdown'], icon: '📁' },
  assets: { title: 'assets/', types: ['image', 'video'],       icon: '📁' },
  audio:  { title: 'audio/',  types: ['audio'],                 icon: '📁' },
  files:  { title: 'files/',  types: ['file'],                  icon: '📁' }
}

function buildFileTree(rawFiles, sessionKey) {
  if (!rawFiles || !rawFiles.length) return []
  const allItems = rawFiles.map(f => mapApiFileToItem(f, 0))

  // 按类型分桶
  const buckets = {}
  for (const key of Object.keys(FOLDER_CONFIG)) buckets[key] = []
  for (let i = 0; i < rawFiles.length; i++) {
    const f = rawFiles[i]
    const item = allItems[i]
    let placed = false
    for (const [key, cfg] of Object.entries(FOLDER_CONFIG)) {
      if (cfg.types.includes(f.type)) {
        buckets[key].push(item)
        placed = true
        break
      }
    }
    if (!placed) buckets.files.push(item)
  }

  // 构建文件树：根目录 + 分类文件夹 + 文件
  const rootChildren = []
  const items = []

  for (const [key, cfg] of Object.entries(FOLDER_CONFIG)) {
    const children = buckets[key]
    if (!children.length) continue
    rootChildren.push(...children)
    const folderId = `${sessionKey}:tree:${key}`
    items.push({
      id: folderId,
      title: cfg.title,
      meta: `${children.length} 个文件`,
      icon: cfg.icon,
      previewMode: 'folder',
      level: 1,
      children
    })
    children.forEach(child => {
      items.push({ ...child, id: `${folderId}:${child.id}`, level: 2 })
    })
  }

  // 根目录
  const root = {
    id: `${sessionKey}:tree:root`,
    title: 'workspace/',
    meta: '完整文件树',
    icon: '📁',
    previewMode: 'folder',
    level: 0,
    children: rootChildren
  }

  return [root, ...items]
}

export default {
  name: 'GroupWorkspacePanel',
  data() {
    return {
      fileTextContent: null,
      agentsLoading: false,
      // VNC iframe 兜底重载：vnc 后端启动慢时第一次 iframe 加载到的是 spinner，
      // 后端 ready 后 iframe 不会自动 refresh，所以 5s 后强制 reload 一次（key 变化触发 Vue 重渲染 iframe DOM）
      vncReloadKey: 0,
      vncReloadTimer: null,
      vncReloadedOnce: false,   // 兜底重载只做一次，避免 realAgents 反复变化导致 VNC 无限重连
      fullscreenPreview: null,
      // 图片预览缩放/拖动（滚轮缩放、按住拖动、双击复位）
      imgZoom: 1,
      imgPanX: 0,
      imgPanY: 0,
      imgDragging: false,
      imgDragStartX: 0,
      imgDragStartY: 0,
      imgPanStartX: 0,
      imgPanStartY: 0,
      previewCtx: { open: false, x: 0, y: 0 },
      cloudToast: { show: false, msg: '', type: 'info' },
      cloudConfirm: { show: false, msg: '', onConfirm: null },
      _fullscreenFetchedText: null,
      wsAudio: { playing: false, current: 0, duration: 0 },
      activeFileFilter: 'all',
      filesPage: 1,
      filesHasMore: true,
      filesLoadingMore: false,
      sheetLoading: false,
      sheetError: null,
      sheetHtml: '',
      sheetNames: [],
      activeSheetName: '',
      _sheetWorkbook: null,
      codeCopied: false,
      collapsedFolders: {},  // { [folderId]: true }
      officePreviewLoading: false,
      aftActiveFolderId: null,   // agent file tree: 当前选中的文件夹id
      aftActiveFileId: null,     // agent file tree: 当前选中的文件id
      changesSelectedFile: '',   // 变更 tab: 筛选文件 id
      changesDropdownOpen: false,
      changesFileQuery: '',
      _pendingChangesFileId: null, // 从历史按钮跳转时暂存的文件 id
      svgDataUrl: '',              // SVG 预览用 data URL
      agentMessages: [],          // Agent 思考过程消息列表
      agentMessagesLoading: false,
      agentMessagesTotal: 0,
      agentMessagesOffset: 0,
      agentThinkingExpanded: {}  // { [agentId]: true/false } 群聊思考面板展开状态
    }
  },
  watch: {
    // 已停用 agent: 若当前 tab 落在已被隐藏的 tab(cloud/files/changes), 自动切到「交付物」
    isCurrentAgentDisabled: {
      immediate: true,
      handler(v) {
        if (v && !['artifacts', 'preview'].includes(this.currentGroupWorkspaceTab)) {
          this.$store.commit('SET_GROUP_WORKSPACE_TAB', 'artifacts')
        }
      }
    },
    streamingThinkingText() {
      // 流式思考时自动滚到底部
      this.$nextTick(() => {
        const el = this.$refs.thinkingBody
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    thinkingMessages() {
      // 新消息加载后滚到底部
      this.$nextTick(() => {
        const el = this.$refs.thinkingBody
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    fullscreenPreviewItem(item) {
      if (item) this.fullscreenPreview = item
    },
    fullscreenPreview() {
      // 打开/换图/关闭都复位缩放与平移
      this.resetImgZoom()
    },
    lastAgentThinking(info) {
      // agent 回复完成，把流式累积的 thinking 直接追加到本地列表
      if (!info || !info.thinking) return
      if (info.conversationId !== this.currentConversationId) return
      const exists = this.agentMessages.some(m => m.id === info.id)
      if (!exists) {
        this.agentMessages = [info, ...this.agentMessages]
        this.agentMessagesTotal++
      }
    },
    fullscreenPreview(item) {
      this._fullscreenFetchedText = null
      if (!item || !item.url) return
      const ext = item.ext || ''
      const needFetch = ext === 'md' || ext === 'html' || ext === 'htm' ||
        ['txt','json','csv','log','xml','yml','yaml','ini','conf','sh','bat',
         'js','ts','css','scss','less','py','java','go','rs','sql',
         'vue','jsx','tsx','php','rb','swift','kt','c','cpp','h','cs','dart','toml'].includes(ext) ||
        (item.mimeType && item.mimeType.startsWith('text/'))
      if (needFetch) {
        const url = item.url
        fetch(url).then(r => r.text()).then(t => {
          if (this.fullscreenPreview && this.fullscreenPreview.url === url) {
            this._fullscreenFetchedText = t
          }
        }).catch(() => {})
      }
    },
    currentGroupWorkspacePreview: {
      immediate: true,
      handler(item) {
      this.fileTextContent = null
      this.svgDataUrl = ''
      this.wsAudio = { playing: false, current: 0, duration: 0 }
      // SVG 文件：fetch 内容转 data URL
      if (item && item.kind === 'image' && item.ext === 'svg' && item.url) {
        fetch(item.url).then(r => r.text()).then(text => {
          this.svgDataUrl = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(text)))
        }).catch(() => { this.svgDataUrl = item.url })
      }
      this.sheetLoading = false
      this.sheetError = null
      this.sheetHtml = ''
      this.sheetNames = []
      this.activeSheetName = ''
      this._sheetWorkbook = null
      this.codeCopied = false
      this.officePreviewLoading = ['ppt', 'pptx', 'doc', 'docx'].includes((item && item.ext) || '')
      // pause + load 强制浏览器重置 source 状态，否则切换文件后旧 source 残留，play() 会抛 NotSupportedError
      this.$nextTick(() => {
        const el = this.$refs.wsAudioEl
        if (el) { el.pause(); el.load() }
      })
      const isText = item && item.url && (
        item.ext === 'txt' || item.ext === 'md' || item.ext === 'json' || item.ext === 'csv' ||
        item.ext === 'log' || item.ext === 'html' || item.ext === 'htm' ||
        ['js','ts','jsx','tsx','css','scss','less','py','java','go','rs','sql','xml','yml','yaml',
         'ini','conf','sh','bat','vue','php','rb','swift','kt','c','cpp','h','cs','dart','toml'].includes(item.ext || '') ||
        (item.mimeType && item.mimeType.startsWith('text/'))
      )
      if (item && item.previewMode === 'file' && isText) {
        fetch(item.url).then(r => r.text()).then(t => { this.fileTextContent = t }).catch(() => {})
      }
      // Excel 文件：动态加载 SheetJS 解析
      const spreadsheetExts = ['xlsx', 'xls', 'ods', 'csv']
      if (item && item.previewMode === 'file' && spreadsheetExts.includes(item.ext || '')) {
        this.loadSheetPreview(item)
      }
    }},
    // 切换到 cloud tab 时自动刷新 agents
    currentGroupWorkspaceTab: {
      handler(tab) {
        if (tab === 'cloud') {
          this.loadAgents()
        }
        if (tab === 'files') {
          this.activeFileFilter = this.currentSessionType !== 'agent' ? 'file' : 'all'
          this._scheduleFilesReload()
        }
        if (tab === 'artifacts') {
          this.activeFileFilter = 'all'
          this._scheduleFilesReload()
        }
        if (tab === 'changes') {
          this.loadChangesFiles()
          if (!this._pendingChangesFileId) {
            this.changesSelectedFile = ''
          }
        }
      },
      immediate: true
    },
    // 群聊切换分类时重新请求后端
    activeFileFilter(val, old) {
      if (this.currentSessionType !== 'agent' && this.currentGroupWorkspaceTab === 'files' && val !== old) {
        this._scheduleFilesReload()
      }
    },
    // 切换会话时：重置文件分类默认值；如果工作区在 files/artifacts tab，刷新一次
    currentSessionKey() {
      // 标记正在切换会话，防止 currentTopicKey watcher 重复调用
      this._sessionSwitching = true
      this.$nextTick(() => { this._sessionSwitching = false })
      if (this.currentGroupWorkspaceTab === 'files') {
        this.activeFileFilter = this.currentSessionType !== 'agent' ? 'file' : 'all'
      }
      this._scheduleFilesReload()
    },
    currentTopicKey(topicKey) {
      if (topicKey && this.currentSessionType === 'agent') {
        if (this.currentGroupWorkspaceTab === 'files') this.refreshFolderTree()
      }
      // 切换话题时重新拉取思考过程和群聊任务计划
      if (topicKey) {
        this.agentMessages = []
        this.agentMessagesTotal = 0
        const currentSession = this.$store.state.sessionItems.find(s => s.key === this.$store.state.currentSessionKey)
        if (this.currentSessionType === 'agent' || (currentSession && currentSession.hasAgent)) {
          this.fetchAgentThinkingMessages()
        }
        // 同一会话内切换话题时拉取（切换会话时由 currentSessionKey watcher 处理，避免重复）
        if (!this._sessionSwitching) this.loadGroupPlans()
      }
    },
    // 文件选择变化时拉取版本历史
    changesDropdownOpen(val) {
      if (val) {
        this._onDocClickChanges = this._onDocClickChanges.bind(this)
        document.addEventListener('click', this._onDocClickChanges)
      } else {
        document.removeEventListener('click', this._onDocClickChanges)
      }
    },
    changesSelectedFile(fileId) {
      if (fileId) {
        this.fetchFileVersions({
          fileId,
          conversationId: this.currentConversationId,
          topicId: this.currentTopicKey || undefined
        })
      }
    },
    // 文件列表加载完成后：优先选中 pending 文件，否则选第一个
    changesAllFiles(list) {
      if (this.currentGroupWorkspaceTab !== 'changes' || list.length === 0) return
      if (this._pendingChangesFileId) {
        this.changesSelectedFile = this._pendingChangesFileId
        this._pendingChangesFileId = null
      } else if (!this.changesSelectedFile) {
        this.changesSelectedFile = list[0].id
      }
    },
    // 切换会话时重置
    currentConversationId: {
      immediate: true,
      handler(convId) {
        this.$store.commit('SET_VNC_MODAL', null)
        this._thinkingInitialized = false
        this.agentMessages = []
        this.agentMessagesTotal = 0
        // 切会话清掉旧的 vnc reload timer + 重置一次性标记，避免刚切走又触发上一个会话的 reload
        this.cancelVncReload()
        this.vncReloadedOnce = false
        if (convId) {
          this.$nextTick(() => {
            const currentSession = this.$store.state.sessionItems.find(s => s.conversationId === convId)
            if (this.currentSessionType === 'agent' || (currentSession && currentSession.hasAgent)) {
              this.fetchAgentThinkingMessages()
            }
          })
        }
        if (this.currentGroupWorkspaceTab === 'cloud') {
          this.loadAgents()
        }
      this.aftActiveFolderId = null
      this.aftActiveFileId = null
      this.changesSelectedFile = ''
      // agent 会话切换时，如果在 files tab，重新拉 folder tree
      if (this.currentSessionType === 'agent' && this.currentGroupWorkspaceTab === 'files') {
        this.$nextTick(() => this.refreshFolderTree())
      }
    }
    },
    // VNC iframe 兜底重载：检测到 agent 列表里出现新的有 gatewayUrl 的 agent，
    // 5s 后强制 reload iframe（覆盖 vnc 后端启动慢导致 iframe 内部一直 spinner 的情况）
    realAgents: {
      handler(list) {
        const hasReadyAgent = (list || []).some(a => !!a.gatewayUrl)
        if (hasReadyAgent) {
          // 仅首次出现可用 agent 时兜底刷一次；之后 realAgents 再变化不再重排（否则无限重连）
          if (!this.vncReloadedOnce) {
            this.vncReloadedOnce = true
            this.scheduleVncReload()
          }
        } else {
          this.vncReloadedOnce = false
          this.cancelVncReload()
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this._escHandler = (e) => {
      if (e.key === 'Escape' && this.fullscreenPreview) { this.closeFullscreen(); return }
      // 图片画廊左右切换
      if (this.galleryActive) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.prevImage() }
        else if (e.key === 'ArrowRight') { e.preventDefault(); this.nextImage() }
      }
    }
    window.addEventListener('keydown', this._escHandler)
  },
  beforeDestroy() {
    if (this._escHandler) window.removeEventListener('keydown', this._escHandler)
    this.cancelVncReload()
    document.removeEventListener('click', this.closePreviewCtx)
    document.removeEventListener('contextmenu', this.closePreviewCtx)
  },
  computed: {
    imgZoomStyle() {
      return {
        transform: `translate(${this.imgPanX}px, ${this.imgPanY}px) scale(${this.imgZoom})`,
        transition: this.imgDragging ? 'none' : 'transform .12s ease',
        cursor: this.imgZoom > 1 ? (this.imgDragging ? 'grabbing' : 'grab') : 'default'
      }
    },
    ...mapState(['currentSessionKey', 'currentSessionType', 'currentGroupWorkspaceTab', 'currentGroupWorkspaceSelections', 'currentGroupWorkspacePreview', 'groupWorkspaceCloudComputers', 'agentSessionProfiles', 'conversationAgents', 'conversationFiles', 'conversationFilesLoading', 'conversationFilesTotal', 'topicFolderTree', 'currentTopicKey', 'conversationArtifactFiles', 'fileVersionsData', 'fileVersionsLoading', 'agentStreamingMessages', 'agentTyping', 'conversationMessages', 'lastAgentThinking', 'fullscreenPreviewItem', 'fullscreenGallery']),
    ...mapGetters(['currentConversationId', 'currentGroupPlans', 'currentSession']),
    // 1对1 Agent 已停用: 云电脑(pod 释放)/全部文件/变更 都没意义, 只保留交付物+预览
    isCurrentAgentDisabled() {
      const s = this.currentSession
      return !!(s && s.type === 'agent' && s.myAgentStatus === 'disabled')
    },
    // ── 图片画廊（左右切换）──────────────────────────────
    galleryActive() {
      const g = this.fullscreenGallery
      return !!(g && this.fullscreenPreview && this.fullscreenPreview.kind === 'image' && g.total > 1)
    },
    galleryLoading() {
      return !!(this.fullscreenGallery && this.fullscreenGallery.loading)
    },
    canPrevImage() {
      const g = this.fullscreenGallery
      return !!(g && g.cur > 0)
    },
    canNextImage() {
      const g = this.fullscreenGallery
      return !!(g && g.cur < g.total - 1)
    },
    galleryCounter() {
      const g = this.fullscreenGallery
      return g ? `${g.cur + 1} / ${g.total}` : ''
    },
    tabs() {
      const isAgent = this.currentSessionType === 'agent'
      // 已停用 agent: 隐藏「云电脑/全部文件/变更」, 只留「交付物 + 预览」
      if (this.isCurrentAgentDisabled) {
        return [
          { key: 'artifacts', label: '交付物' },
          { key: 'preview', label: '预览' }
        ]
      }
      return [
        { key: 'cloud', label: '云电脑' },
        { key: 'artifacts', label: '交付物' },
        { key: 'files', label: this.currentSessionType === 'group' ? '群文件' : '全部文件' },
        { key: 'changes', label: '变更' },
        { key: 'preview', label: '预览' }
      ]
    },
    // 所有有 thinking 的消息，按时间正序（接口返回倒序，需要反转）
    thinkingMessages() {
      return this.agentMessages.filter(m => m.thinking).slice().reverse()
    },
    // 实时流式文本（只显示 thinking，content 在气泡里显示）
    streamingThinkingText() {
      const convId = this.currentConversationId
      if (!convId) return ''
      const key = this.currentTopicKey ? convId + ':' + this.currentTopicKey : convId
      const streaming = this.agentStreamingMessages[key]
      if (!streaming) return ''
      return streaming.thinking || ''
    },
    // 兼容旧逻辑（标题/badge 判断用）
    currentThinkingText() {
      return this.streamingThinkingText || (this.thinkingMessages.length ? 'has' : '')
    },
    agentMessagesHasMore() {
      return this.agentMessages.length < this.agentMessagesTotal
    },
    isAgentThinking() {
      const convId = this.currentConversationId
      if (!convId) return false
      const key = this.currentTopicKey ? convId + ':' + this.currentTopicKey : convId
      const streaming = this.agentStreamingMessages[key]
      if (streaming && streaming.thinking) return true
      return !!(this.agentTyping[key])
    },
    wsAudioProgress() {
      if (!this.wsAudio.duration) return 0
      return Math.min(100, (this.wsAudio.current / this.wsAudio.duration) * 100)
    },
    wsAudioTimeStr() {
      const fmt = t => {
        if (!t || isNaN(t)) return '0:00'
        return Math.floor(t / 60) + ':' + String(Math.floor(t % 60)).padStart(2, '0')
      }
      return fmt(this.wsAudio.current) + ' / ' + fmt(this.wsAudio.duration)
    },
    isSpreadsheetPreview() {
      const p = this.currentGroupWorkspacePreview
      if (!p) return false
      const ext = p.ext || ''
      return ['xlsx', 'xls', 'ods'].includes(ext)
    },
    isOfficePreview() {
      const p = this.currentGroupWorkspacePreview
      if (!p) return false
      const ext = p.ext || ''
      return ['ppt', 'pptx', 'doc', 'docx'].includes(ext)
    },
    officeViewerUrl() {
      const p = this.currentGroupWorkspacePreview
      if (!p || !p.url) return ''
      // 同时尝试 Google Docs Viewer（对公网URL兼容性更好）
      return `https://docs.google.com/viewer?url=${encodeURIComponent(p.url)}&embedded=true`
    },
    isHtmlPreview() {
      const p = this.currentGroupWorkspacePreview
      if (!p) return false
      return p.ext === 'html' || p.ext === 'htm'
    },
    isMarkdownPreview() {
      const p = this.currentGroupWorkspacePreview
      return p && p.ext === 'md'
    },
    isTextPreview() {
      const p = this.currentGroupWorkspacePreview
      if (!p) return false
      const ext = p.ext || ''
      const textExts = ['txt', 'json', 'csv', 'log', 'xml', 'yml', 'yaml', 'ini', 'conf',
        'sh', 'bat', 'js', 'ts', 'css', 'scss', 'less', 'py', 'java', 'go', 'rs', 'sql',
        'vue', 'jsx', 'tsx', 'php', 'rb', 'swift', 'kt', 'c', 'cpp', 'h', 'cs', 'dart',
        'toml', 'env', 'gitignore', 'dockerfile', 'makefile', 'lock']
      return textExts.includes(ext) || (p.mimeType && p.mimeType.startsWith('text/') && ext !== 'html' && ext !== 'htm')
    },
    // 全屏弹窗：是否是 Excel/spreadsheet 类型
    fullscreenIsSpreadsheet() {
      const p = this.fullscreenPreview
      if (!p) return false
      const ext = p.ext || ''
      return ['xlsx', 'xls', 'ods'].includes(ext)
    },
    // 全屏弹窗：是否是文本/代码类型
    fullscreenIsMarkdown() {
      const p = this.fullscreenPreview
      return p && p.ext === 'md'
    },
    fullscreenIsText() {
      const p = this.fullscreenPreview
      if (!p) return false
      const ext = p.ext || ''
      const textExts = ['txt', 'json', 'csv', 'log', 'xml', 'yml', 'yaml', 'ini', 'conf',
        'sh', 'bat', 'js', 'ts', 'css', 'scss', 'less', 'py', 'java', 'go', 'rs', 'sql',
        'vue', 'jsx', 'tsx', 'php', 'rb', 'swift', 'kt', 'c', 'cpp', 'h', 'cs', 'dart',
        'toml', 'env', 'gitignore', 'dockerfile', 'makefile', 'lock']
      return textExts.includes(ext) || (p.mimeType && p.mimeType.startsWith('text/') && ext !== 'html' && ext !== 'htm')
    },
    // 全屏弹窗：复用右侧已拉取的 fileTextContent（同一个文件）
    fullscreenTextContent() {
      const p = this.fullscreenPreview
      if (!p) return null
      // 优先复用右侧已拉取的内容
      const cur = this.currentGroupWorkspacePreview
      if (cur && p.url === cur.url && this.fileTextContent) return this.fileTextContent
      // 否则用全屏独立拉取的内容
      return this._fullscreenFetchedText
    },
    fullscreenLineCount() {
      if (!this.fullscreenTextContent) return 0
      return this.fullscreenTextContent.split('\n').length
    },
    fullscreenHighlighted() {
      const raw = this.fullscreenTextContent
      if (!raw) return ''
      const ext = (this.fullscreenPreview && this.fullscreenPreview.ext) || ''
      const escaped = raw.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      // 只对代码类文件做高亮，纯文本（txt 等）直接返回转义内容
      const codeExts = ['js', 'ts', 'jsx', 'tsx', 'css', 'scss', 'less', 'py', 'json', 'xml',
        'yml', 'yaml', 'sh', 'sql', 'java', 'go', 'rs', 'vue', 'php', 'rb', 'swift',
        'kt', 'c', 'cpp', 'h', 'cs', 'dart', 'toml', 'md']
      if (!codeExts.includes(ext)) return escaped
      return escaped
        .replace(/(&lt;\/?)([\w-]+)/g, '$1<span class="hl-tag">$2</span>')
        .replace(/(=)(&#x27;[^]*?&#x27;|&quot;[^]*?&quot;)/g, '$1<span class="hl-string">$2</span>')
        .replace(/(&#x27;[^\n]*?&#x27;|&quot;[^\n]*?&quot;|`[^`]*`)/g, '<span class="hl-string">$1</span>')
        .replace(/\b(\d+(\.\d+)?)\b/g, '<span class="hl-number">$1</span>')
        .replace(/(\/\/[^\n]*|#(?![\w-])[^\n]*|&lt;!--[\s\S]*?--&gt;)/g, '<span class="hl-comment">$1</span>')
        .replace(/\b(const|let|var|function|return|if|else|for|while|do|switch|case|break|continue|class|import|export|from|default|async|await|try|catch|finally|throw|new|this|super|typeof|instanceof|in|of|def|self|print|lambda|yield|pass|with|as|True|False|None|null|undefined|true|false|type|interface|extends|implements|abstract|public|private|protected|static|void|int|float|str|bool|string|number|any|never|unknown|SELECT|FROM|WHERE|INSERT|UPDATE|DELETE|CREATE|TABLE|JOIN|ON|AND|OR|NOT|IN|IS|AS|template|script|style|ref|computed|watch|props|emit|setup|data|methods|mounted|created|components)\b/g, '<span class="hl-keyword">$1</span>')
    },
    codeLineCount() {
      if (!this.fileTextContent) return 0
      return this.fileTextContent.split('\n').length
    },
    codeClass() {
      const ext = (this.currentGroupWorkspacePreview && this.currentGroupWorkspacePreview.ext) || ''
      const map = { js: 'language-js', ts: 'language-ts', css: 'language-css', py: 'language-python', json: 'language-json', xml: 'language-xml', yml: 'language-yaml', yaml: 'language-yaml', sh: 'language-bash', sql: 'language-sql', java: 'language-java', go: 'language-go', rs: 'language-rust', md: 'language-markdown' }
      return map[ext] || ''
    },
    highlightedCode() {
      if (!this.fileTextContent) return ''
      const ext = (this.currentGroupWorkspacePreview && this.currentGroupWorkspacePreview.ext) || ''
      const raw = this.fileTextContent
      // 轻量级语法高亮：escape HTML 后对常见 token 着色，无需外部库
      const escaped = raw.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      // 只对代码类文件做高亮，纯文本直接返回
      const codeExts = ['js', 'ts', 'jsx', 'tsx', 'css', 'scss', 'less', 'py', 'json', 'xml',
        'yml', 'yaml', 'sh', 'sql', 'java', 'go', 'rs', 'vue', 'php', 'rb', 'swift',
        'kt', 'c', 'cpp', 'h', 'cs', 'dart', 'toml', 'md']
      if (!codeExts.includes(ext)) return escaped
      return escaped
        // HTML/Vue 标签
        .replace(/(&lt;\/?)([\w-]+)/g, '$1<span class="hl-tag">$2</span>')
        // 属性值（带引号）
        .replace(/(=)(&#x27;[^&#x27;]*&#x27;|&quot;[^&quot;]*&quot;)/g, '$1<span class="hl-string">$2</span>')
        // 普通字符串（单双引号、模板字符串）
        .replace(/(&#x27;[^&#x27;\n]*&#x27;|&quot;[^&quot;\n]*&quot;|`[^`]*`)/g, '<span class="hl-string">$1</span>')
        // 数字
        .replace(/\b(\d+(\.\d+)?)\b/g, '<span class="hl-number">$1</span>')
        // 注释 // # <!-- -->
        .replace(/(\/\/[^\n]*|#(?![\w-])[^\n]*|&lt;!--[\s\S]*?--&gt;)/g, '<span class="hl-comment">$1</span>')
        // 关键字
        .replace(/\b(const|let|var|function|return|if|else|for|while|do|switch|case|break|continue|class|import|export|from|default|async|await|try|catch|finally|throw|new|this|super|typeof|instanceof|in|of|def|self|print|lambda|yield|pass|with|as|True|False|None|null|undefined|true|false|type|interface|extends|implements|abstract|public|private|protected|static|void|int|float|str|bool|string|number|any|never|unknown|SELECT|FROM|WHERE|INSERT|UPDATE|DELETE|CREATE|TABLE|JOIN|ON|AND|OR|NOT|IN|IS|AS|template|script|style|ref|computed|watch|props|emit|setup|data|methods|mounted|created|components)\b/g, '<span class="hl-keyword">$1</span>')
    },
    agentProfile() {
      if (this.currentSessionType === 'agent') {
        return this.agentSessionProfiles[this.currentSessionKey] || null
      }
      return null
    },
    realAgents() {
      const convId = this.currentConversationId
      if (!convId) return []
      // 已停用 agent 的 pod 已释放, 云电脑接管/重启都没意义, 整张卡片不显示
      return (this.conversationAgents[convId] || []).filter(a => a.id && a.status !== 'disabled')
    },
    agentSyntheticFiles() {
      const p = this.agentProfile
      if (!p) return []
      const sk = p.key || this.currentSessionKey
      const role = p.role || (p.name.includes('-') ? p.name.split('-').slice(1).join('-') : '') || 'Agent'
      const wt = p.workspaceTitle || `${p.name}的电脑`
      const rt = p.reportTitle || `${p.name} 工作总结`
      const st = p.state || 'empty'
      return [
        { id: `${sk}:artifact:summary`, title: `${wt} - 方案摘要.md`, meta: `${p.name} · ${role}`, icon: '文', tag: '生成文档', previewMode: 'doc', opensPreview: true, desc: `${rt}，当前状态：${st}` },
        { id: `${sk}:artifact:result`, title: `${rt}.md`, meta: `最终成果 · ${st}`, icon: '文', tag: '生成文档', previewMode: 'doc', opensPreview: true, desc: `${p.name} 当前正在围绕「${wt}」推进。` },
        { id: `${sk}:change:plan`, title: `${wt}-变更记录.diff`, meta: `${p.name} · 文件变更`, icon: '改', tag: '配置', previewMode: 'diff', opensPreview: true },
        { id: `${sk}:code:workspace`, title: `${wt}-workspace.js`, meta: `${p.name} · 预览逻辑`, icon: '码', tag: '代码文件', previewMode: 'code', opensPreview: true },
        { id: `${sk}:web:preview`, title: `${wt}-预览页面.html`, meta: `${p.name} · 页面预览`, icon: '页', tag: '代码文件', previewMode: 'code', opensPreview: true }
      ]
    },
    agentChangeEntries() {
      const f = this.agentSyntheticFiles
      return {
        created: [f[0], f[1], f[3], f[4]].filter(Boolean).map(x => ({ ...x, changeLabel: '新建' })),
        modified: [f[2]].filter(Boolean).map(x => ({ ...x, changeLabel: '修改' })),
        deleted: []
      }
    },
    changesAllFiles() {
      const convId = this.currentConversationId
      if (!convId) return []
      return (this.conversationArtifactFiles[convId] || []).map(f => ({
        id: f.id,
        title: f.fileName || f.name || '未命名文件'
      }))
    },
    changesSelectedFileName() {
      if (!this.changesSelectedFile) return ''
      const f = this.changesAllFiles.find(f => f.id === this.changesSelectedFile)
      return f ? f.title : ''
    },
    filteredChangesFiles() {
      const q = (this.changesFileQuery || '').trim().toLowerCase()
      if (!q) return this.changesAllFiles
      return this.changesAllFiles.filter(f => (f.title || '').toLowerCase().includes(q))
    },
    changesVersionData() {
      if (!this.changesSelectedFile) return null
      return this.fileVersionsData[this.changesSelectedFile] || null
    },
    changesTotalCount() {
      const d = this.changesVersionData
      return d && d.stats ? (d.stats.total || 0) : 0
    },
    changesTodayCount() {
      const d = this.changesVersionData
      return d && d.stats ? (d.stats.today || 0) : 0
    },
    changesWeekCount() {
      const d = this.changesVersionData
      return d && d.stats ? (d.stats.last7Days || 0) : 0
    },
    changesGroupedByDate() {
      const d = this.changesVersionData
      if (!d || !d.dailyVersions || !d.dailyVersions.length) return []
      const selectedTitle = (this.changesAllFiles.find(f => f.id === this.changesSelectedFile) || {}).title || ''
      return d.dailyVersions.map(group => {
        const dt = new Date(group.date)
        const fmtDate = (v) => v.getFullYear() + '/' + String(v.getMonth() + 1).padStart(2, '0') + '/' + String(v.getDate()).padStart(2, '0')
        const items = (group.versions || []).map(v => {
          const senderName = (v.createdByName || v.senderName || v.author || 'Agent')
          const senderRole = v.createdByRole || v.role || ''
          const senderPosition = v.createdByPosition || v.position || ''
          const authorParts = [senderName, senderRole, senderPosition].filter(Boolean)
          return {
            id: v.id || v.versionId || (group.date + '-' + Math.random()),
            title: selectedTitle || v.fileName || '',
            author: authorParts.join('·'),
            time: v.createdAt || v.time || '',
            summary: v.summary || v.description || v.changeSummary || '',
            url: v.fileUrl || v.url || '',
            _date: v.createdAt || group.date
          }
        })
        return {
          date: group.date,
          dateLabel: fmtDate(dt),
          items,
          _ts: dt.getTime()
        }
      }).sort((a, b) => b._ts - a._ts)
    },
    cloudComputers() {
      if (this.agentProfile) {
        const p = this.agentProfile
        const sk = this.currentSessionKey
        const role = p.role || (p.name.includes('-') ? p.name.split('-').slice(1).join('-') : '') || 'Agent'
        return [{
          id: `${sk}:cloud:main`,
          title: `${p.name} · 云电脑`,
          owner: p.name,
          meta: `${role} · ${p.workspaceTitle || p.name + '的电脑'}`,
          icon: '云',
          previewMode: 'agent-cloud'
        }]
      }
      const raw = this.groupWorkspaceCloudComputers[this.currentSessionKey] || []
      return raw.map((c, i) => ({
        id: `${this.currentSessionKey}:cloud:${i}`,
        title: c.title || `${c.owner} 的电脑`,
        meta: `${c.subtitle} · ${c.task}`,
        tag: c.status || '在线',
        tagClass: cloudStatusClass(c.status),
        icon: '💻',
        owner: c.owner
      }))
    },
    workspaceItems() {
      const tab = this.currentGroupWorkspaceTab
      if (tab === 'cloud') return this.cloudComputers
      if (tab === 'preview') return this.currentGroupWorkspacePreview ? [this.currentGroupWorkspacePreview] : []
      if (this.agentProfile) return this.agentTabItems
      return this.groupTabStaticItems
    },
    agentTabItems() {
      const tab = this.currentGroupWorkspaceTab
      const sk = this.currentSessionKey
      const p = this.agentProfile
      const files = this.agentSyntheticFiles
      const changeEntries = this.agentChangeEntries
      const role = p.role || (p.name.includes('-') ? p.name.split('-').slice(1).join('-') : '') || 'Agent'
      const wt = p.workspaceTitle || `${p.name}的电脑`
      if (tab === 'artifacts') {
        return [
          { id: `${sk}:artifact:overview`, title: '交付物总览', meta: `${p.name} · ${role}`, icon: '总', previewMode: 'artifact-overview', changeEntries },
          { id: `${sk}:artifact:list`, title: '当前会话交付物', meta: `${p.name} · ${wt}`, icon: '产', previewMode: 'artifact-list', files },
          ...files.slice(0, 3)
        ]
      }
      if (tab === 'files') {
        const tree = this.currentFolderTree
        if (!tree || !tree.folders || !tree.folders.length) {
          return [{ id: `${sk}:folder:root`, title: wt, meta: `${p.name} · 工作目录`, icon: '📁', previewMode: 'folder', children: [] }]
        }
        return tree.folders.map(folder => ({
          id: `${sk}:folder:${folder.id}`,
          folderId: folder.id,
          title: folder.name === '/' ? '根目录' : folder.name,
          meta: `${folder.files.length} 个文件`,
          icon: folder.name === '/' ? '📂' : '📁',
          previewMode: 'folder',
          folderFiles: folder.files
        }))
      }
      if (tab === 'changes') {
        return [
          { id: `${sk}:changes:overview`, title: '本次会话的文件修改记录', meta: `${p.name} · 文件变更`, icon: '改', previewMode: 'change-overview', changeEntries },
          ...changeEntries.created,
          ...changeEntries.modified
        ]
      }
      return []
    },
    apiFileItems() {
      const convId = this.currentConversationId
      if (!convId) return []
      const rawFiles = this.conversationFiles[convId] || []
      return buildFileTree(rawFiles, this.currentSessionKey || convId)
    },
    firstTopicId() {
      // agent 会话没有 currentTopicKey 时，从 sessionTopicPages 取第一个 topic id 作为 fallback
      const item = this.sessionItems.find(s => s.key === this.currentSessionKey)
      if (!item || !item.topicKey) return null
      const page = this.sessionTopicPages[item.topicKey]
      return page?.topics?.[0]?.key || page?.activeKey || null
    },
    flatApiFiles() {
      const convId = this.currentConversationId
      if (!convId) return []
      const raw = this.conversationFiles[convId] || []
      // 链接分类：后端返回 links 结构不同，直接透传
      if (this.currentSessionType !== 'agent' && this.activeFileFilter === 'link') {
        return raw.map(l => ({
          id: l.id || l.url,
          title: l.title || '',
          url: l.url || '',
          content: l.content || '',
          sender: l.sender || null,
          kind: 'link'
        }))
      }
      return raw.map(f => mapApiFileToItem(f, 0))
    },
    currentFolderTree() {
      const topicId = this.currentTopicKey || this.firstTopicId
      if (!topicId) return null
      const raw = this.topicFolderTree[topicId]
      if (!raw) return null

      const mapFile = (f) => ({
        id: f.id,
        name: f.fileName || f.name || '',
        url: f.fileUrl || f.url || '',
        fileSize: f.fileSize || 0,
        mimeType: f.mimeType || '',
        createdBy: f.createdByType === 'agent' ? 'Agent' : (f.createdByName || ''),
        fileType: f.fileType || '',
        isAgent: f.createdByType === 'agent'
      })

      const flatFolders = []

      // rootFiles 直接放根级别
      const rootFiles = (raw.rootFiles || []).map(mapFile)
      if (rootFiles.length) {
        flatFolders.push({ id: '__root__', name: '/', path: '/', depth: 0, isRoot: true, files: rootFiles })
      }

      // 递归展开 folders 树，文件直接从 folder.files 取
      const walkFolder = (folder) => {
        if (folder.depth === 0 && folder.name === '/') {
          for (const child of (folder.children || [])) walkFolder(child)
          return
        }
        flatFolders.push({
          id: folder.id,
          name: folder.name,
          path: folder.path || folder.name,
          depth: folder.depth,
          isRoot: false,
          files: (folder.files || []).map(mapFile)
        })
        for (const child of (folder.children || [])) walkFolder(child)
      }

      for (const folder of (raw.folders || [])) walkFolder(folder)

      return flatFolders.length ? { folders: flatFolders } : null
    },
    aftActiveFolder() {
      const tree = this.currentFolderTree
      if (!tree || !this.aftActiveFolderId) return null
      return tree.folders.find(f => f.id === this.aftActiveFolderId) || null
    },
    aftActiveFile() {
      const folder = this.aftActiveFolder
      if (!folder || !this.aftActiveFileId) return null
      return folder.files.find(f => f.id === this.aftActiveFileId) || null
    },
    aftBreadcrumb() {
      if (this.aftActiveFileId && this.aftActiveFile) return this.aftActiveFile.name
      if (this.aftActiveFolderId && this.aftActiveFolder) {
        const n = this.aftActiveFolder.name
        return n === '/' ? '根目录' : n + '/'
      }
      return ''
    },
    filteredApiFiles() {
      const filter = this.activeFileFilter
      // 非 agent 会话：后端已过滤，直接返回
      if (this.currentSessionType !== 'agent' && (filter === 'media' || filter === 'file' || filter === 'link')) {
        return this.flatApiFiles
      }
      if (filter === 'all') return this.flatApiFiles
      // 非群聊客户端分类
      if (filter === 'doc') return this.flatApiFiles.filter(f =>
        f.kind === 'file' || ['document', 'markdown'].includes(f.kind) ||
        ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'md', 'csv'].includes(f.ext))
      if (filter === 'image') return this.flatApiFiles.filter(f => f.kind === 'image')
      if (filter === 'video') return this.flatApiFiles.filter(f => f.kind === 'video')
      return this.flatApiFiles
    },
    filesTotal() {
      const convId = this.currentConversationId
      if (!convId) return 0
      return this.conversationFilesTotal[convId] || 0
    },
    artifactFilters() {
      return [
        { key: 'all',   label: '全部' },
        { key: 'doc',   label: '文档' },
        { key: 'image', label: '图片' },
        { key: 'video', label: '视频' }
      ]
    },
    fileFilters() {
      if (this.currentSessionType !== 'agent') {
        return [
          { key: 'file',  label: '文件' },
          { key: 'media', label: '媒体' },
          { key: 'link',  label: '链接' }
        ]
      }
      return [
        { key: 'all',   label: '全部' },
        { key: 'doc',   label: '文档' },
        { key: 'image', label: '图片' },
        { key: 'video', label: '视频' }
      ]
    },
    groupTabStaticItems() {
      const tab = this.currentGroupWorkspaceTab
      if (tab === 'artifacts') return [
        { id: 'art-1', title: '奖励到账排查说明.md', icon: '📄', meta: 'Markdown · 17 KB', tag: '文档', opensPreview: true },
        { id: 'art-2', title: '用户回复话术.html', icon: '🌐', meta: 'HTML · 21 KB', tag: '代码', opensPreview: true }
      ]
      if (tab === 'files') return this.apiFileItems
      if (tab === 'changes') return [
        { id: 'chg-1', title: '奖励到账排查说明.md', icon: '📝', meta: '修改于 12:06', tag: '修改', opensPreview: true }
      ]
      return []
    },
    selectedItem() {
      const tab = this.currentGroupWorkspaceTab
      if (tab === 'preview') return this.currentGroupWorkspacePreview
      const selId = this.currentGroupWorkspaceSelections[tab]
      const items = this.workspaceItems
      return items.find(i => i.id === selId) || items[0] || null
    },
    tabMeta() {
      const meta = TAB_META[this.currentGroupWorkspaceTab] || TAB_META.artifacts
      if (this.currentGroupWorkspaceTab === 'files' && this.currentSessionType === 'group') {
        return { ...meta, title: '群文件' }
      }
      return meta
    },
    sidebarTitle() { return this.tabMeta.title },
    sidebarSub() { return this.tabMeta.sub },
    previewFile() {
      if (this.currentGroupWorkspaceTab === 'cloud') return '云电脑'
      if (this.currentGroupWorkspaceTab === 'preview') {
        return this.currentGroupWorkspacePreview ? (this.currentGroupWorkspacePreview.title || this.currentGroupWorkspacePreview.name) : '等待预览'
      }
      return this.selectedItem ? (this.selectedItem.title || this.selectedItem.name) : '等待预览'
    },
    codeLines() {
      const item = this.currentGroupWorkspacePreview
      if (!item) return []
      if (item.code) return String(item.code).split('\n')
      const wt = this.agentProfile?.workspaceTitle || item.title || '工作区'
      const name = this.agentProfile?.name || '当前用户'
      const state = this.agentProfile?.state || 'empty'
      return [
        `// ${wt}`,
        `const profile = '${name}';`,
        `const status = '${state}';`,
        'renderCloudWorkspace();',
        'syncArtifactsPreview();',
        'commitLatestOutput();'
      ]
    },
    previewMeta() {
      if (this.currentGroupWorkspaceTab === 'cloud') return '鼠标移入电脑框时显示"全屏查看 / 接管电脑"。'
      if (this.currentGroupWorkspaceTab === 'preview') {
        return this.currentGroupWorkspacePreview ? (this.currentGroupWorkspacePreview.meta || '') : '点击左侧文件或会话内文件开始预览'
      }
      return this.selectedItem ? (this.selectedItem.meta || '') : '点击左侧文件或会话内文件开始预览'
    }
  },
  methods: {
    ...mapActions(['fetchConversationFiles', 'fetchTopicFolderTree', 'fetchFileVersions', 'fetchArtifactFiles']),
    closeFullscreen() {
      this.fullscreenPreview = null
      this.$store.commit('SET_FULLSCREEN_PREVIEW', null)
      this.$store.commit('CLEAR_FULLSCREEN_GALLERY')   // 关预览即清画廊，防串图
      this.resetImgZoom()
    },
    // ── 图片画廊（左右切换）──────────────────────────────
    // 后端 image item → 预览项（带画廊定位/游标字段，与 TelegramChat._galleryPreviewItem 一致）
    _galleryPreviewItem(it) {
      const ext = (it.fileName || '').split('.').pop().toLowerCase()
      return {
        id: 'chat-file-preview', previewMode: 'file',
        title: it.fileName || '图片预览', meta: '',
        url: it.url, kind: 'image',
        fileName: it.fileName || '', mimeType: it.mimeType || 'image/*',
        fileSizeStr: '', ext,
        _messageId: it.messageId, _fileIndex: (it.fileIndex == null ? 0 : it.fileIndex), _seq: it.seq
      }
    },
    // 切到全序列绝对下标 absIndex 的图（需已在 loaded 窗口内）
    _showGalleryAt(absIndex) {
      const g = this.fullscreenGallery
      if (!g) return
      const item = g.loaded[absIndex - g.baseIndex]
      if (!item) return
      this.$store.commit('SET_GALLERY_CUR', absIndex)
      this.$store.commit('SET_FULLSCREEN_PREVIEW', item)
      this.resetImgZoom()
    },
    // 向某方向加载更多（'after' 更新 / 'before' 更早）；返回是否加载到新图
    async _loadMoreGallery(dir) {
      const g = this.fullscreenGallery
      if (!g || g.loading) return false
      this.$store.commit('SET_GALLERY_LOADING', true)
      try {
        const params = dir === 'after'
          ? { afterSeq: g.loaded[g.loaded.length - 1]._seq, limit: 30 }
          : { beforeSeq: g.loaded[0]._seq, limit: 30 }
        const data = await api.getTopicImages(g.convId, g.topicId, params)
        const d = (data && data.items) ? data : (data && data.data) ? data.data : data
        // 去重：按 messageId+fileIndex 过滤掉已在窗口里的（防游标边界重叠导致下标错位）
        const seen = new Set((this.fullscreenGallery.loaded || []).map(p => p._messageId + '#' + p._fileIndex))
        const items = ((d && d.items) || [])
          .map(it => this._galleryPreviewItem(it))
          .filter(p => !seen.has(p._messageId + '#' + p._fileIndex))
        if (dir === 'after') this.$store.commit('APPEND_GALLERY', { items, hasNewer: !!(d && d.hasNewer) })
        else this.$store.commit('PREPEND_GALLERY', { items, hasOlder: !!(d && d.hasOlder) })
        return items.length > 0
      } catch (e) {
        console.warn('[gw] 加载更多图片失败', e)
        return false
      } finally {
        this.$store.commit('SET_GALLERY_LOADING', false)
      }
    },
    // 离 loaded 窗口边缘 ≤2 张且还有更多 → 后台预取下一页（不阻塞当前切换）
    _maybePrefetchGallery() {
      const g = this.fullscreenGallery
      if (!g || g.loading) return
      const localIdx = g.cur - g.baseIndex
      if (g.hasNewer && localIdx >= g.loaded.length - 2) this._loadMoreGallery('after')
      else if (g.hasOlder && localIdx <= 1) this._loadMoreGallery('before')
    },
    async nextImage() {
      const g = this.fullscreenGallery
      if (!g || g.loading || g.cur >= g.total - 1) return
      const target = g.cur + 1
      if (target - g.baseIndex >= g.loaded.length) {
        if (!g.hasNewer) return
        const ok = await this._loadMoreGallery('after')
        if (!ok || (this.fullscreenGallery && target - this.fullscreenGallery.baseIndex >= this.fullscreenGallery.loaded.length)) return
      }
      this._showGalleryAt(target)
      this._maybePrefetchGallery()
    },
    async prevImage() {
      const g = this.fullscreenGallery
      if (!g || g.loading || g.cur <= 0) return
      const target = g.cur - 1
      if (target - g.baseIndex < 0) {
        if (!g.hasOlder) return
        const ok = await this._loadMoreGallery('before')
        if (!ok || (this.fullscreenGallery && target - this.fullscreenGallery.baseIndex < 0)) return
      }
      this._showGalleryAt(target)
      this._maybePrefetchGallery()
    },
    resetImgZoom() {
      this.imgZoom = 1
      this.imgPanX = 0
      this.imgPanY = 0
      this.imgDragging = false
      this.closePreviewCtx()
    },
    onModalBackdropClick() {
      // 菜单开着时这一下只关菜单（document click 已处理），不连带关弹窗
      if (this.previewCtx.open) return
      this.closeFullscreen()
    },
    onPreviewContextMenu(e) {
      this.previewCtx = { open: true, x: e.clientX, y: e.clientY }
      setTimeout(() => {
        document.addEventListener('click', this.closePreviewCtx)
        document.addEventListener('contextmenu', this.closePreviewCtx)
      }, 0)
    },
    closePreviewCtx() {
      if (this.previewCtx.open) this.previewCtx.open = false
      document.removeEventListener('click', this.closePreviewCtx)
      document.removeEventListener('contextmenu', this.closePreviewCtx)
    },
    async doCopyPreviewImage() {
      this.closePreviewCtx()
      const fp = this.fullscreenPreview
      if (!fp) return
      const url = (fp.ext === 'svg' && this.svgDataUrl) ? this.svgDataUrl : fp.url
      await copyImageToClipboard(url)
    },
    doDownloadPreview() {
      this.closePreviewCtx()
      if (this.fullscreenPreview) this.downloadFile(this.fullscreenPreview)
    },
    doClosePreview() {
      this.closePreviewCtx()
      this.closeFullscreen()
    },
    onPreviewWheel(e) {
      const fp = this.fullscreenPreview
      if (!fp || fp.kind !== 'image') return
      e.preventDefault()
      const img = e.currentTarget.querySelector('.gw-fullscreen-img')
      if (!img) return
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15
      const next = Math.min(6, Math.max(1, this.imgZoom * factor))
      if (next === this.imgZoom) return
      if (next === 1) { this.resetImgZoom(); return }
      // 以光标为锚点缩放：保持光标下的点不动
      const rect = img.getBoundingClientRect()
      const ox = e.clientX - (rect.left + rect.width / 2)
      const oy = e.clientY - (rect.top + rect.height / 2)
      this.imgPanX += ox * (1 - next / this.imgZoom)
      this.imgPanY += oy * (1 - next / this.imgZoom)
      this.imgZoom = next
    },
    onPreviewMouseDown(e) {
      const fp = this.fullscreenPreview
      if (!fp || fp.kind !== 'image' || this.imgZoom <= 1) return
      e.preventDefault()
      this.imgDragging = true
      this.imgDragStartX = e.clientX
      this.imgDragStartY = e.clientY
      this.imgPanStartX = this.imgPanX
      this.imgPanStartY = this.imgPanY
    },
    onPreviewMouseMove(e) {
      if (!this.imgDragging) return
      this.imgPanX = this.imgPanStartX + (e.clientX - this.imgDragStartX)
      this.imgPanY = this.imgPanStartY + (e.clientY - this.imgDragStartY)
    },
    onPreviewMouseUp() {
      if (this.imgDragging) this.imgDragging = false
    },
    onPreviewDblClick() {
      const fp = this.fullscreenPreview
      if (!fp || fp.kind !== 'image') return
      this.resetImgZoom()
    },
    onChangesItemClick(item) {
      if (!item.url) return
      const name = (item.title || '').toLowerCase()
      const ext = name.split('.').pop()
      this.setTab('preview')
      this.$store.commit('SET_GROUP_WORKSPACE_PREVIEW', {
        title: item.title,
        url: item.url,
        ext,
        previewMode: 'file',
        fileName: item.title,
        meta: item.author + ' · ' + item.time
      })
    },
    downloadChangesItem(item) {
      if (!item.url) return
      // 通过 fetch + blob 下载，兼容跨域
      fetch(item.url).then(r => r.blob()).then(blob => {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = item.title || 'file'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }).catch(() => {
        // 降级直接打开
        window.open(item.url, '_blank')
      })
    },
    aftSelectFolder(folder) {
      this.aftActiveFolderId = folder.id
      this.aftActiveFileId = null
    },
    aftSelectFile(file, folder) {
      this.aftActiveFolderId = folder.id
      this.aftActiveFileId = file.id
      // 直接跳预览
      this.previewFolderFile(file)
    },
    aftFileIconLabel(name) {
      const ext = (name || '').split('.').pop().toLowerCase()
      if (['jpg','jpeg','png','gif','webp','svg'].includes(ext)) return '图'
      if (['mp4','mov','avi','mkv'].includes(ext)) return '视'
      if (['mp3','wav','m4a','ogg'].includes(ext)) return '音'
      if (ext === 'pdf') return 'PDF'
      if (['doc','docx'].includes(ext)) return '文'
      if (['xls','xlsx','csv'].includes(ext)) return '表'
      if (['ppt','pptx'].includes(ext)) return 'PPT'
      if (['js','ts','jsx','tsx','vue','py','java','go','rs','cpp','c','h','cs','html','css','json','yml','yaml','sh','md'].includes(ext)) return '</>'
      return '文'
    },
    aftFileIconClass(name) {
      const ext = (name || '').split('.').pop().toLowerCase()
      if (['jpg','jpeg','png','gif','webp','svg'].includes(ext)) return 'aft-ic-image'
      if (['mp4','mov','avi','mkv'].includes(ext)) return 'aft-ic-video'
      if (['mp3','wav','m4a','ogg'].includes(ext)) return 'aft-ic-audio'
      if (ext === 'pdf') return 'aft-ic-pdf'
      if (['doc','docx'].includes(ext)) return 'aft-ic-doc'
      if (['xls','xlsx','csv'].includes(ext)) return 'aft-ic-data'
      if (['ppt','pptx'].includes(ext)) return 'aft-ic-ppt'
      if (['js','ts','jsx','tsx','vue','py','java','go','rs','cpp','c','h','cs'].includes(ext)) return 'aft-ic-code'
      if (['html','css','json','yml','yaml','sh','md','txt'].includes(ext)) return 'aft-ic-doc'
      return 'aft-ic-file'
    },
    refreshFolderTree() {
      if (this.currentSessionType !== 'agent') return
      const topicId = this.currentTopicKey || this.firstTopicId
      if (topicId) {
        this.$store.dispatch('fetchTopicFolderTree', topicId)
      }
    },
    toggleFolder(folderId) {
      if (this.collapsedFolders[folderId]) {
        this.$delete(this.collapsedFolders, folderId)
      } else {
        this.$set(this.collapsedFolders, folderId, true)
      }
    },
    folderFileExt(filename) {
      return (filename || '').split('.').pop().toLowerCase()
    },
    folderFileIcon(filename) {
      const ext = this.folderFileExt(filename)
      if (['jpg','jpeg','png','gif','webp','svg'].includes(ext)) return 'image'
      if (['mp4','mov','avi','mkv'].includes(ext)) return 'video'
      if (['mp3','wav','m4a','ogg'].includes(ext)) return 'audio'
      if (ext === 'pdf') return 'pdf'
      if (['md','txt','log'].includes(ext)) return 'text'
      if (['js','ts','py','java','go','rs','cpp','c','html','css','json','yaml','yml','sh'].includes(ext)) return 'code'
      return 'file'
    },
    async downloadFolderFile(file) {
      if (!file || !file.url) return
      try {
        const res = await fetch(file.url)
        const blob = await res.blob()
        const blobUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = file.name || 'file'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
      } catch {
        // fetch 失败时降级用 window.open
        window.open(file.url, '_blank')
      }
    },
    previewFolderFile(file) {
      if (!file) return
      const ext = (file.name || '').split('.').pop().toLowerCase()
      const mimeType = file.mimeType || ''
      const _audioExts = ['mp3','wav','ogg','aac','flac','wma','m4a']
      const _videoExts = ['mp4','webm','mkv','avi','mov']
      const _imageExts = ['jpg','jpeg','png','gif','webp','svg','bmp']
      let kind = 'file'
      if (mimeType.startsWith('image/')) kind = 'image'
      else if (mimeType.startsWith('video/')) kind = 'video'
      else if (mimeType.startsWith('audio/')) kind = 'audio'
      else if (_imageExts.includes(ext)) kind = 'image'
      else if (_audioExts.includes(ext)) kind = 'audio'
      else if (_videoExts.includes(ext)) kind = 'video'
      this.$store.commit('SET_GROUP_WORKSPACE_PREVIEW', {
        previewMode: 'file',
        id: file.id || 'folder-file-preview',
        url: file.url,
        fileName: file.name,
        fileSizeStr: file.fileSize ? (file.fileSize < 1024 ? file.fileSize + ' B' : file.fileSize < 1048576 ? (file.fileSize / 1024).toFixed(1) + ' KB' : (file.fileSize / 1048576).toFixed(1) + ' MB') : '',
        mimeType,
        ext,
        kind,
        meta: file.createdBy || ''
      })
      this.$store.commit('SET_GROUP_WORKSPACE_TAB', 'preview')
      this.$store.commit('SET_WORKSPACE_OPEN', true)
    },
    // VNC iframe 兜底重载：5s 后强制 reload 一次（vncReloadKey++ → :key 变化 → Vue 重建 iframe DOM → 重新拉 vnc URL）
    scheduleVncReload() {
      this.cancelVncReload()
      this.vncReloadTimer = setTimeout(() => {
        this.vncReloadKey += 1
        this.vncReloadTimer = null
      }, 5000)
    },
    cancelVncReload() {
      if (this.vncReloadTimer) {
        clearTimeout(this.vncReloadTimer)
        this.vncReloadTimer = null
      }
    },
    async loadAgents() {
      const convId = this.currentConversationId
      if (!convId) return
      this.agentsLoading = true
      await this.$store.dispatch('fetchConversationAgents', convId)
      this.agentsLoading = false
      // 检查是否有待触发的全屏查看/接管
      const pendingId = this.$store.state.pendingVncAgentId
      if (pendingId) {
        const takeover = this.$store.state.pendingVncTakeover
        this.$store.state.pendingVncAgentId = null
        this.$store.state.pendingVncTakeover = false
        const agents = this.$store.state.conversationAgents[convId] || []
        const agent = agents.find(a => a.id === pendingId)
        if (agent) {
          this.$nextTick(() => {
            if (takeover) this.takeoverAgent(agent)
            else this.enterFullscreen(agent)
          })
        }
      }
    },
    selectChangesFile(id) {
      this.changesSelectedFile = id
      this.changesDropdownOpen = false
      this.changesFileQuery = ''
    },
    _onDocClickChanges(e) {
      if (this.changesDropdownOpen && !e.target.closest('.gw-changes-custom-select')) {
        this.changesDropdownOpen = false
      }
    },
    async loadChangesFiles() {
      const convId = this.currentConversationId
      if (!convId) return
      await this.fetchArtifactFiles({ conversationId: convId })
    },
    async fetchAgentThinkingMessages(append) {
      const convId = this.currentConversationId
      if (!convId) return
      this.agentMessagesLoading = true
      try {
        const offset = append ? this.agentMessages.length : 0
        const data = await api.getAgentMessages(convId, {
          topicId: this.currentTopicKey || undefined,
          limit: 50,
          offset
        })
        // 会话已切走，丢弃过期结果
        if (this.currentConversationId !== convId) return
        const msgs = data && Array.isArray(data.messages) ? data.messages : []
        this.agentMessagesTotal = data && data.total ? data.total : msgs.length
        if (append) {
          this.agentMessages = [...this.agentMessages, ...msgs]
        } else {
          // 合并：保留本地已追加但 API 未返回的 thinking（后端可能还没存好）
          const apiIds = new Set(msgs.map(m => m.id))
          const localOnly = this.agentMessages.filter(m => !apiIds.has(m.id))
          this.agentMessages = [...localOnly, ...msgs]
        }
      } catch (e) {
        console.warn('[GroupWorkspacePanel] fetchAgentThinkingMessages failed:', e.message)
      } finally {
        this.agentMessagesLoading = false
      }
    },
    loadMoreThinking() {
      if (!this.agentMessagesLoading && this.agentMessagesHasMore) {
        this.fetchAgentThinkingMessages(true)
      }
    },
    onThinkingScroll(e) {
      // 滚动到顶部时加载更多
      if (e.target.scrollTop === 0 && this.agentMessagesHasMore && !this.agentMessagesLoading) {
        const oldHeight = e.target.scrollHeight
        this.fetchAgentThinkingMessages(true).then(() => {
          this.$nextTick(() => {
            // 保持滚动位置
            e.target.scrollTop = e.target.scrollHeight - oldHeight
          })
        })
      }
    },
    formatThinkingTime(isoStr) {
      if (!isoStr) return ''
      const d = new Date(isoStr)
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      const hh = String(d.getHours()).padStart(2, '0')
      const mi = String(d.getMinutes()).padStart(2, '0')
      return `${mm}-${dd} ${hh}:${mi}`
    },
    // 群聊：获取某个 agent 的思考记录列表（最近3条，正序）
    getAgentThinkingList(agentId) {
      return this.agentMessages
        .filter(m => m.thinking && m.sender && m.sender.id === agentId)
        .slice(0, 3)
        .reverse()
    },
    // 群聊：获取某个 agent 的实时流式文本
    getAgentStreamingText(agentId) {
      const convId = this.currentConversationId
      if (!convId) return ''
      const key = this.currentTopicKey ? convId + ':' + this.currentTopicKey : convId
      const streaming = this.agentStreamingMessages[key]
      if (!streaming) return ''
      if (streaming.agentId && streaming.agentId !== agentId) return ''
      return streaming.thinking || ''
    },
    toggleAgentThinking(agentId) {
      this.$set(this.agentThinkingExpanded, agentId, !this.agentThinkingExpanded[agentId])
    },
    getAgentLatestTime(agentId) {
      const list = this.agentMessages.filter(m => m.thinking && m.sender && m.sender.id === agentId)
      if (list.length && list[0].createdAt) {
        const d = new Date(list[0].createdAt)
        return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0')
      }
      return ''
    },
    loadGroupPlans() {
      const state = this.$store.state
      const convId = this.currentConversationId
      const item = state.sessionItems.find(s => s.key === this.currentSessionKey)
      if (!item || item.type !== 'group' || !item.hasAgent || !convId) return
      // 取 topicId：优先 currentTopicKey，其次话题页 activeKey，最后第一个话题
      let topicId = state.currentTopicKey
      if (!topicId && item.topicKey) {
        const page = state.sessionTopicPages[item.topicKey]
        if (page) topicId = page.activeKey
        if (!topicId && page && page.topics && page.topics.length) {
          topicId = page.topics[0].key || page.topics[0].id
        }
      }
      if (topicId) {
        this.$store.dispatch('fetchGroupPlans', { conversationId: convId, topicId })
      }
    },
    getAgentGroupPlan(agentId) {
      return this.currentGroupPlans.find(p => p.agentId === agentId) || null
    },
    getAgentPlanStepsVisible(agentId) {
      const plan = this.getAgentGroupPlan(agentId)
      if (!plan || !plan.metadata || !plan.metadata.steps) return []
      const steps = plan.metadata.steps
      if (this.agentThinkingExpanded[agentId]) return steps
      return steps.length > 3 ? steps.slice(0, 3) : steps
    },
    truncateThinking(text) {
      if (!text) return ''
      const clean = text.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim()
      return clean.length > 60 ? clean.slice(0, 60) + '…' : clean
    },
    renderMd(text) {
      return renderMarkdown(text || '')
    },
    // 同一 tick 内多次调用只跑一次 loadFiles，防止 watcher 之间互相触发导致的重复请求
    _scheduleFilesReload() {
      const tab = this.currentGroupWorkspaceTab
      if (tab !== 'files' && tab !== 'artifacts') return
      if (this._filesReloadPending) return
      this._filesReloadPending = true
      this.$nextTick(() => {
        this._filesReloadPending = false
        const t = this.currentGroupWorkspaceTab
        if (t !== 'files' && t !== 'artifacts') return
        if (t === 'files' && this.currentSessionType === 'agent') {
          this.refreshFolderTree()
        } else {
          this.loadFiles()
        }
      })
    },
    async loadFiles(append) {
      const convId = this.currentConversationId
      if (!convId) return
      const tab = this.currentGroupWorkspaceTab
      let type = 'all'
      if (tab === 'artifacts') {
        type = 'agent'
      } else if (this.currentSessionType !== 'agent') {
        type = this.activeFileFilter // media / file / link
      }
      if (!append) {
        this.filesPage = 1
        this.filesHasMore = true
      }
      const pageSize = 50
      await this.fetchConversationFiles({ conversationId: convId, pageSize, type, page: this.filesPage, append })
      // 判断是否还有更多
      const loaded = (this.conversationFiles[convId] || []).length
      const total = this.conversationFilesTotal[convId] || 0
      if (loaded >= total || loaded < this.filesPage * pageSize) {
        this.filesHasMore = false
      }
    },
    async loadMoreFiles() {
      if (!this.filesHasMore || this.filesLoadingMore) return
      this.filesLoadingMore = true
      this.filesPage++
      await this.loadFiles(true)
      this.filesLoadingMore = false
    },
    onFilesScroll(e) {
      const el = e.target
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
        this.loadMoreFiles()
      }
    },
    openLink(item) {
      const url = item.url || ''
      if (url) window.open(url, '_blank')
    },
    linkInitial(title) {
      if (!title) return '🔗'
      return title.charAt(0).toUpperCase()
    },
    linkIconColor(str) {
      if (!str) return '#8B5CF6'
      let h = 0
      for (let i = 0; i < str.length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0
      const hue = ((h % 360) + 360) % 360
      return 'hsl(' + hue + ', 55%, 55%)'
    },
    agentOwner(agent) {
      if (!agent) return ''
      return agent.ownerName || agent.owner_name || agent.owner || ''
    },
    vncUrl(agent) {
      // 处理 Unicode 转义和 URL 编码，与 Flutter 端逻辑一致
      let raw = agent.gatewayUrl || ''
      if (raw.includes('\\u')) {
        raw = raw.replace(/\\u([0-9a-fA-F]{4})/g, (_, h) => String.fromCharCode(parseInt(h, 16)))
      }
      if (raw.includes('%')) {
        try { raw = decodeURIComponent(raw) } catch {}
      }
      return `/novnc/vnc.html?url=${encodeURIComponent(raw)}`
    },
    enterFullscreen(agent) {
      this.$store.commit('SET_VNC_MODAL', { url: this.vncUrl(agent), agentName: agent.name, takeover: false })
    },
    takeoverAgent(agent) {
      this.$store.commit('SET_VNC_MODAL', { url: this.vncUrl(agent), agentName: agent.name, takeover: true })
    },
    async restartOpenClaw(agent) {
      if (!agent || !agent.id) return
      const vncDomain = agent.vnc_domain || agent.vncDomain || ''
      if (!vncDomain) { this.showCloudToast('无法获取 vnc_domain，请先连接一次 VNC', 'error'); return }
      if (this._cloudConfirmPending) return
      this.showCloudConfirm('确定重启 Agent？正在运行的任务会中断。', async () => {
        this.$store.commit('SET_CLOUD_ACTION_BUSY', 'Agent 正在重启中…')
        try {
          await api.restartOpenClaw(agent.id, vncDomain)
          this.showCloudToast('重启成功', 'success')
        } catch (e) {
          this.showCloudToast(e.message || '重启失败', 'error')
        } finally {
          this.$store.commit('SET_CLOUD_ACTION_BUSY', '')
        }
      })
    },
    async rescueOpenClaw(agent) {
      if (!agent || !agent.id) return
      const vncDomain = agent.vnc_domain || agent.vncDomain || ''
      if (!vncDomain) { this.showCloudToast('无法获取 vnc_domain，请先连接一次 VNC', 'error'); return }
      if (this._cloudConfirmPending) return
      this.showCloudConfirm('确定恢复出厂？这会强制重建容器，正在运行的任务和终端会话将中断！', async () => {
        this.$store.commit('SET_CLOUD_ACTION_BUSY', 'OpenClaw 正在恢复出厂设置中…')
        try {
          await api.rescueOpenClaw(agent.id, vncDomain)
          this.showCloudToast('恢复出厂成功', 'success')
        } catch (e) {
          this.showCloudToast(e.message || '恢复出厂失败', 'error')
        } finally {
          this.$store.commit('SET_CLOUD_ACTION_BUSY', '')
        }
      })
    },
    showCloudToast(msg, type) {
      this.cloudToast = { show: true, msg, type: type || 'info' }
      if (this._cloudToastTimer) clearTimeout(this._cloudToastTimer)
      this._cloudToastTimer = setTimeout(() => { this.cloudToast.show = false }, 3000)
    },
    showCloudConfirm(msg, onConfirm) {
      this.cloudConfirm = { show: true, msg, onConfirm }
    },
    resolveCloudConfirm(ok) {
      const fn = this.cloudConfirm.onConfirm
      this.cloudConfirm = { show: false, msg: '', onConfirm: null }
      if (ok && fn) fn()
    },
    setTab(key) {
      this.$store.commit('SET_GROUP_WORKSPACE_TAB', key)
    },
    isItemSelected(item) {
      return this.selectedItem && this.selectedItem.id === item.id
    },
    selectItem(item) {
      if (item.opensPreview) {
        this.$store.commit('SET_GROUP_WORKSPACE_PREVIEW', item)
        this.$store.commit('SET_GROUP_WORKSPACE_TAB', 'preview')
      } else {
        this.$store.commit('SET_GROUP_WORKSPACE_SELECTION', { tab: this.currentGroupWorkspaceTab, id: item.id })
      }
    },
    openPreview(item) {
      this.$store.commit('SET_GROUP_WORKSPACE_PREVIEW', item)
      this.$store.commit('SET_GROUP_WORKSPACE_TAB', 'preview')
    },
    wsTogglePlay() {
      const el = this.$refs.wsAudioEl
      if (!el) return
      if (el.paused) {
        el.play().then(() => {
          this.wsAudio.playing = true
        }).catch(err => {
          // NotSupportedError: src 未就绪或格式不支持，重置状态避免 UI 卡在"播放中"
          console.warn('[audio] play() failed:', err.message)
          this.wsAudio.playing = false
        })
      } else {
        el.pause()
        this.wsAudio.playing = false
      }
    },
    wsSeek(e) {
      const el = this.$refs.wsAudioEl
      if (!el || !this.wsAudio.duration) return
      const rect = e.currentTarget.getBoundingClientRect()
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      el.currentTime = ratio * this.wsAudio.duration
    },
    gwIconClass(item) {
      if (item.kind === 'image') return 'gw-icon-image'
      if (item.kind === 'video') return 'gw-icon-video'
      if (item.kind === 'audio') return 'gw-icon-audio'
      const ext = item.ext || ''
      if (ext === 'pdf') return 'gw-icon-pdf'
      if (ext === 'doc' || ext === 'docx') return 'gw-icon-doc'
      if (ext === 'xls' || ext === 'xlsx') return 'gw-icon-xls'
      if (ext === 'ppt' || ext === 'pptx') return 'gw-icon-ppt'
      if (ext === 'mp3' || ext === 'wav' || ext === 'm4a') return 'gw-icon-audio'
      if (ext === 'mp4' || ext === 'mov' || ext === 'avi') return 'gw-icon-video'
      if (ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'gif' || ext === 'webp') return 'gw-icon-image'
      return 'gw-icon-file'
    },
    copyCode() {
      if (!this.fileTextContent) return
      navigator.clipboard && navigator.clipboard.writeText(this.fileTextContent).then(() => {
        this.codeCopied = true
        setTimeout(() => { this.codeCopied = false }, 2000)
      })
    },
    // ── Excel / Spreadsheet 预览 ─────────────────────────
    async _ensureXLSX() {
      if (window.XLSX) return window.XLSX
      return new Promise((resolve, reject) => {
        const s = document.createElement('script')
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js'
        s.onload = () => resolve(window.XLSX)
        s.onerror = () => reject(new Error('SheetJS 加载失败'))
        document.head.appendChild(s)
      })
    },
    _sheetToHtml(wb, sheetName) {
      const ws = wb.Sheets[sheetName]
      if (!ws) return '<p>空工作表</p>'
      const XLSX = window.XLSX
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1')
      const maxRow = Math.min(range.e.r, 199)   // 最多渲染 200 行
      const maxCol = Math.min(range.e.c, 49)     // 最多渲染 50 列
      let html = '<table class="ws-sheet-table"><thead>'
      for (let R = range.s.r; R <= maxRow; R++) {
        html += R === range.s.r ? '<tr>' : (R === range.s.r + 1 ? '</thead><tbody><tr>' : '<tr>')
        for (let C = range.s.c; C <= maxCol; C++) {
          const addr = XLSX.utils.encode_cell({ r: R, c: C })
          const cell = ws[addr]
          const val = cell ? (cell.w !== undefined ? cell.w : String(cell.v ?? '')) : ''
          const escaped = String(val).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
          html += R === range.s.r ? `<th>${escaped}</th>` : `<td>${escaped}</td>`
        }
        html += '</tr>'
      }
      html += '</tbody></table>'
      if (range.e.r > maxRow) html += `<p class="ws-sheet-truncated">仅显示前 200 行（共 ${range.e.r + 1} 行）</p>`
      return html
    },
    async loadSheetPreview(item) {
      if (!item || !item.url) return
      this.sheetLoading = true
      this.sheetError = null
      this.sheetHtml = ''
      try {
        const XLSX = await this._ensureXLSX()
        const resp = await fetch(item.url)
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
        const buf = await resp.arrayBuffer()
        const wb = XLSX.read(buf, { type: 'array' })
        this._sheetWorkbook = wb
        this.sheetNames = wb.SheetNames
        this.activeSheetName = wb.SheetNames[0]
        this.sheetHtml = this._sheetToHtml(wb, this.activeSheetName)
      } catch (e) {
        this.sheetError = '预览失败：' + e.message
      } finally {
        this.sheetLoading = false
      }
    },
    switchSheet(name) {
      if (!this._sheetWorkbook || !name) return
      this.activeSheetName = name
      this.sheetHtml = this._sheetToHtml(this._sheetWorkbook, name)
    },
    openFileHistory(item) {
      if (!item || !item.id) return
      // 暂存文件 id，切换 tab 后由 watcher 自动选中并拉取版本历史
      this._pendingChangesFileId = item.id
      if (this.currentGroupWorkspaceTab === 'changes') {
        // 已在变更 tab，直接选中
        this.changesSelectedFile = item.id
        this._pendingChangesFileId = null
      } else {
        this.$store.commit('SET_GROUP_WORKSPACE_TAB', 'changes')
      }
    },
    async downloadFile(item) {
      if (!item || !item.url) return
      try {
        const res = await fetch(item.url)
        const blob = await res.blob()
        const blobUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = item.fileName || 'download'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000)
      } catch {
        // fetch 失败时降级直接打开
        window.open(item.url, '_blank')
      }
    }
  }
}
</script>

<style>
/* GroupWorkspacePanel 独立样式
   从 global.css 拆分到此处，webpack 会将其打包进独立 chunk，
   仅在该组件首次渲染时才注入页面，减少首屏 CSS 体积。 */
@import '../styles/group-workspace.css';
</style>
